import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePricing from '../../hooks/usePricing';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  ExpandMore,
  Grass as LawnIcon,
  LocalFlorist as GardeningIcon,
  AcUnit as SnowIcon,
  ContentCut as TrimmingIcon,
  WbSunny as SpringIcon,
  AutoAwesome as FallIcon,
  Nature as TurfIcon,
  Business as BusinessIcon,
  LocalShipping as ParkingLotIcon,
  AccountBox as ContactIcon,
  Search as InspectionIcon,
  Save,
  Cancel,
  InfoOutlined,
  CloudUpload as UploadIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { createApiUrl, makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrencyWithSymbol } from '../../utils/currencyUtils';
import { calcWeeks } from '../../utils/pricingCalculations';
import {
  getBasePricesForSquareFt,
  calculateTotalTurfPricing,
  formatTurfPricingForDisplay,
  migrateLegacyTurfPricing
} from '../../utils/turfPricingUtils';
import SiteDiagramUpload from './SiteDiagramUpload';
import SqFtCalculatorAdornment from './SqFtCalculator';

// Divisor in every legacy snow formula (PricesHelper.cs
// `SnowCalculationConstant1 = 60`, Quotas.com.js `snowCalculationConstant1 = 60`).
// Legacy hardcodes it; it is not an Admin Settings rate. Must match
// backend/routes/pricing.js SNOW_CALCULATION_CONSTANT.
const SNOW_CALCULATION_CONSTANT = 60;

// Legacy stored commercial Extra Conditions as rich-text HTML; show it as
// plain text (paragraphs separated by blank lines) in the editor.
const conditionsToText = (value) => {
  const raw = String(value || '');
  if (!/<[a-z][\s\S]*>/i.test(raw)) return raw;
  const doc = new DOMParser().parseFromString(
    raw
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n\n')
      .replace(/<li[^>]*>/gi, '• '),
    'text/html'
  );
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
};

// Billing seasons (legacy PropertyInfo Summer/Winter Start/End). The billed
// month count is the calendar-month difference (legacy MyExtensions.TotalMonths),
// with one bill per month after the season start: Apr 15 - Nov 15 bills
// May 15 ... Nov 15 = 7 months. Defaults must match backend
// quote-pdf-builder.js resolveSeasonDates.
const SEASON_DATE_KEYS = ['summerStartDate', 'summerEndDate', 'winterStartDate', 'winterEndDate'];

const parseYmd = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return m ? { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) } : null;
};

const defaultBillingSeasons = (contractStart) => {
  const year = parseYmd(contractStart)?.year || new Date().getFullYear();
  return {
    summerStartDate: `${year}-04-15`,
    summerEndDate: `${year}-11-15`,
    winterStartDate: `${year}-11-15`,
    winterEndDate: `${year + 1}-04-15`
  };
};

const seasonMonthCount = (start, end) => {
  const s = parseYmd(start);
  const e = parseYmd(end);
  if (!s || !e) return 0;
  return ((e.year - s.year) * 12) + e.month - s.month;
};

const isValidSeason = (start, end) => {
  const months = seasonMonthCount(start, end);
  return months > 0 && months <= 12 && String(end) > String(start);
};

// Legacy DateTime.AddMonths clamps to the last day of the target month.
const seasonBillingDates = (start, end) => {
  if (!isValidSeason(start, end)) return [];
  const s = parseYmd(start);
  const months = seasonMonthCount(start, end);
  return Array.from({ length: months }, (_, i) => {
    const monthIndex = s.month + i + 1;
    const lastDay = new Date(s.year, monthIndex + 1, 0).getDate();
    return new Date(s.year, monthIndex, Math.min(s.day, lastDay));
  });
};

const withSeasonMonths = (info) => (
  isValidSeason(info.summerStartDate, info.summerEndDate)
    ? { ...info, summerMonths: seasonMonthCount(info.summerStartDate, info.summerEndDate) }
    : info
);

const BillingSeasonRow = ({ idPrefix, title, start, end, onChange, locale, t }) => {
  const hasBoth = Boolean(start && end);
  const valid = hasBoth && isValidSeason(start, end);
  const months = valid ? seasonMonthCount(start, end) : 0;
  const billDates = seasonBillingDates(start, end)
    .map(d => d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }));
  const startLabel = t('quoteForm.billingSeasons.start', 'Start');
  const endLabel = t('quoteForm.billingSeasons.end', 'End');
  const monthsText = t('quoteForm.billingSeasons.monthsBilled', '{{count}} month(s) billed', { count: months });

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      </Box>
      <Grid container spacing={2} sx={{ p: 2 }} alignItems="flex-start">
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="date"
            id={`${idPrefix}-start`}
            label={startLabel}
            value={start || ''}
            onChange={e => onChange('start', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': `${title} - ${startLabel}` }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="date"
            id={`${idPrefix}-end`}
            label={endLabel}
            value={end || ''}
            onChange={e => onChange('end', e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={hasBoth && !valid}
            helperText={hasBoth && !valid
              ? t('quoteForm.billingSeasons.invalidRange', 'End must be after the start and no more than 12 months later')
              : ''}
            inputProps={{ 'aria-label': `${title} - ${endLabel}` }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Box role="status" aria-live="polite" aria-label={`${title}: ${valid ? monthsText : '--'}`}>
            <Chip
              color={valid ? 'primary' : 'default'}
              variant={valid ? 'filled' : 'outlined'}
              label={valid ? monthsText : t('quoteForm.billingSeasons.noMonths', '-- months')}
              sx={{ fontWeight: 700 }}
            />
            {billDates.length > 0 && (
              <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 1 }}>
                {t('quoteForm.billingSeasons.billedOn', 'Billed on')}: {billDates.join(', ')}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// TKT-229 (Doug §5.B): single-source the snow service total "pricing box" so
// Plowing, Shoveling and Salt Application all render identically — same box
// style, size, font weight, colour and centered alignment — instead of salt
// using a smaller, differently-placed treatment. Any future snow service total
// can reuse this for a consistent look.
const SnowPricingTotalBox = ({ label, value, belowMin = false, minSeasonal = 0, t }) => (
  <Box sx={{ textAlign: 'center', p: 1, bgcolor: belowMin ? '#fff3e0' : '#e8f5e8', borderRadius: 1, border: belowMin ? '1px solid #ff9800' : 'none' }}>
    <Typography variant="body2" fontWeight="bold">{label}</Typography>
    <Typography variant="h6" color={belowMin ? 'warning.main' : 'success.main'}>{formatCurrencyWithSymbol(value)}</Typography>
    {belowMin && (
      <Typography variant="caption" color="warning.main">
        {t('quoteForm.snow.seasonalMinApplied', 'Seasonal minimum applied')} ({formatCurrencyWithSymbol(minSeasonal)})
      </Typography>
    )}
  </Box>
);

const TurfProgramRow = ({
  selected,
  onToggle,
  label,
  ariaLabel,
  applications,
  priceInfo,
  muValue,
  onMuChange,
  muAriaLabel,
  t
}) => (
  <Box sx={{
    mb: 3,
    p: 2,
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    backgroundColor: selected ? '#e8f5e8' : 'transparent'
  }}>
    <Grid container spacing={2} alignItems="center">
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selected}
              onChange={onToggle}
              aria-label={ariaLabel}
            />
          }
          label={label}
        />
        {priceInfo?.pricePerApplication > 0 && (
          <Typography variant="body2" sx={{ ml: 4, color: '#666', fontStyle: 'italic' }}>
            {formatCurrencyWithSymbol(priceInfo.pricePerApplication)} {t('quoteForm.turf.perApplication', 'per application')} x {applications} {t('quoteForm.turf.applications', 'applications')}
          </Typography>
        )}
      </Grid>
      <Grid size={{ xs: 6, md: 2 }}>
        <TextField
          label={t('quoteForm.turf.markupPercent', 'MU %')}
          type="number"
          size="small"
          value={muValue}
          onChange={onMuChange}
          inputProps={{ min: 0, step: 0.1, 'aria-label': muAriaLabel }}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 6, md: 4 }}>
        {priceInfo?.price > 0 ? (
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: selected ? '#2e7d32' : '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: selected ? 'white' : '#666', fontWeight: 'bold' }}>
              {t('quoteForm.turf.seasonalPrice', 'Seasonal Price')}
            </Typography>
            <Typography variant="h6" sx={{ color: selected ? 'white' : '#2e7d32' }}>
              {formatCurrencyWithSymbol(priceInfo.price)}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#999' }}>{t('quoteForm.turf.notSelected', 'Not Selected')}</Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  </Box>
);

// While focused the price box shows exactly what the rep typed (including
// empty); falling back to the calculated price mid-edit made it impossible to
// clear the box and type a new amount. Empty on blur = use the calculated price.
const TurfAddonRow = ({
  checked,
  onToggle,
  label,
  ariaLabel,
  price,
  overrideValue,
  onOverrideChange,
  overrideAriaLabel,
  t
}) => {
  const [draft, setDraft] = useState(null);
  const shown = overrideValue > 0 ? overrideValue : (price || '');

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    const cleaned = raw.replace(/(\..*)\./g, '$1');
    setDraft(cleaned);
    onOverrideChange({ target: { value: cleaned === '.' ? '' : cleaned } });
  };

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        gap: 2,
        p: 1
      }}>
        <FormControlLabel
          sx={{ flex: 1, mr: 0 }}
          control={
            <Checkbox
              checked={checked}
              onChange={onToggle}
              aria-label={ariaLabel}
            />
          }
          label={label}
        />
        <TextField
          label={t('quoteForm.turf.seasonalPrice', 'Seasonal Price')}
          size="small"
          value={draft !== null ? draft : String(shown)}
          onFocus={e => { setDraft(String(shown)); e.target.select(); }}
          onChange={handleChange}
          onBlur={() => setDraft(null)}
          inputProps={{ inputMode: 'decimal', 'aria-label': overrideAriaLabel }}
          sx={{ width: { xs: '100%', sm: 150 } }}
        />
      </Box>
    </Grid>
  );
};

// Hidden snow sections must not keep pricing or PDFs. Residential quotes
// keep the Basic / Premium / Premium + Salt package. Commercial quotes, and
// multi-residential or industrial properties, keep the commercial snow lines.
// Construction stays on the shared form until Naomi defines that split.
function applySnowVisibility(servicesState, { construction, residentialPackages, commercialSections }) {
  if (construction || !servicesState?.snow) return servicesState;
  const snow = { ...servicesState.snow };
  if (!residentialPackages) {
    snow.program = 0;
    snow.baseSum = 0;
  }
  if (!commercialSections) {
    [
      'parkingLotDrivewayPlowingEnabled',
      'parkingLotParkingLotPlowingEnabled',
      'shovelingCitySidewalkEnabled',
      'shovelingInternalSidewalkEnabled',
      'shovelingDrivewayEnabled',
      'truckParkingLotSaltingEnabled',
      'truckDrivewaySaltingEnabled',
      'crewCitySidewalkSaltingEnabled',
      'crewInternalSidewalkSaltingEnabled',
      'crewDrivewaySaltingEnabled',
      'saltBinsEnabled',
      'premiumSaltingEnabled',
      'perEventEnabled',
      'trashBagEnabled',
      'allInclusive',
    ].forEach((key) => { snow[key] = false; });
    snow.relocationPrice = 0;
  }
  return { ...servicesState, snow };
}

// [section, billed-time field, crew work-time field] pairs kept in sync.
const WORK_TIME_SYNC_FIELDS = [
  ['lawn', 'timeMinutes', 'workTimeMinutes'],
  ['spring', 'timeMinutes', 'workTimeMinutes'],
  ['fall', 'timeMinutes', 'workTimeMinutes'],
  ['trimming', 'timeMinutes', 'workTimeMinutes'],
  ['gardening', 'deWeedingTime', 'deWeedingWorkTime'],
  ['gardening', 'soilTillingTime', 'soilTillingWorkTime'],
  ['gardening', 'edgeTime', 'edgeWorkTime'],
];

const workTimeSyncSnapshot = (services) => Object.fromEntries(
  WORK_TIME_SYNC_FIELDS.map(([section, pricingField]) => [
    `${section}.${pricingField}`,
    services?.[section]?.[pricingField],
  ])
);

const UniversalQuoteForm = ({ quote, onSuccess, onCancel, quoteType = 'residential' }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  
  // Quote type configuration
  const isResidential = quoteType === 'residential';
  const isCommercial = quoteType === 'commercial';
  const isConstruction = quoteType === 'construction';
  
  const config = {
    title: isCommercial ? 'Commercial Quote' : 
           isConstruction ? 'Construction Quote' : 'Residential Quote',
    color: isCommercial ? '#2196f3' : 
           isConstruction ? '#ff9800' : '#4caf50',
    quoteTypeId: isCommercial ? 2 : isConstruction ? 3 : 1,
    defaultPropertySize: isCommercial ? 5000 : 1000,
    defaultParkingLotSize: isCommercial ? 2000 : 0,
    defaultGardenSize: isCommercial ? 500 : 0,
    defaultLawnTime: isCommercial ? 120 : 60,
    showBilling: isCommercial,
    showParkingLot: isCommercial,
    commonAreaDefault: isCommercial
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [translatingReminder, setTranslatingReminder] = useState(false);
  const [reminderTranslateError, setReminderTranslateError] = useState('');
  const [createdQuoteId, setCreatedQuoteId] = useState(null);
  const [pendingSiteDiagram, setPendingSiteDiagram] = useState(null);
  const [pendingSiteDiagramPreview, setPendingSiteDiagramPreview] = useState('');
  const [lookups, setLookups] = useState({ customers: [], propertyTypes: [] });
  // Aug. 16 clarification supersedes TKT-228: every section on a brand-new
  // quote starts collapsed, including Commercial Snow Removal. Existing
  // quotes still expand their enabled service sections after they load.
  const [expandedSections, setExpandedSections] = useState([]);
  
  // Customer search state
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [adjustmentDraft, setAdjustmentDraft] = useState('');
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [adjustmentSaving, setAdjustmentSaving] = useState(false);
  const pendingAdjustmentRef = useRef(null);
  
  // Turf pricing state (automatic calculations)
  const [turfPricing, setTurfPricing] = useState({ total: 0, breakdown: {}, programName: 'None' });
  const [turfBasePrices, setTurfBasePrices] = useState(null);
  const [turfPricingLoading, setTurfPricingLoading] = useState(false);

  // Dynamic service rates for local calculations
  const [serviceRates, setServiceRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(false);

  useEffect(() => () => {
    if (pendingSiteDiagramPreview) {
      URL.revokeObjectURL(pendingSiteDiagramPreview);
    }
  }, [pendingSiteDiagramPreview]);

  // Admin Settings defaults the quote form copies into a new quote's service
  // configs (times, disposal fees, salt prices, run counts).
  const rateDefaults = (data) => ({
    jetBlue: data['SnowJetBlueSaltCost']?.fixed || 50,
    ecoSalt: data['SnowEcoSaltCost']?.fixed || 50,
    // TKT-294: a season covers ~30 truck runs and ~60 crew runs. Crew
    // salting is done by the shoveling crew, so it follows the crew run
    // count rather than the truck one.
    defaultRuns: data['SnowTruckDefaultRuns']?.fixed || 30,
    defaultSqftPerMin: data['SnowSaltingCrewSqftPerMin']?.fixed || 65,
    defaultLotPlowingRunTime: data['SnowLotPlowingRunTime']?.basic || data['SnowLotPlowingRunTime']?.fixed || 30,
    defaultShovelCrewRunTime: data['SnowShovelCrewRunTime']?.basic || data['SnowShovelCrewRunTime']?.fixed || 60,
    defaultRelocationPrice: data['SnowRelocationPrice']?.basic || data['SnowRelocationPrice']?.fixed || 0,
    defaultFallTimeMinutes: data['FallTimes']?.basic || data['FallTimes']?.fixed || 240,
    defaultFallDisposalFee: data['FallDisposalFee']?.basic || data['FallDisposalFee']?.fixed || 0,
    defaultTrimmingApplications: data['TrimApplications']?.basic || data['TrimApplications']?.fixed || 1,
    defaultTrimmingTimeMinutes: data['TrimTime']?.basic || data['TrimTime']?.fixed || 120,
    defaultTrimmingDisposalFee: data['TrimDisposalFee']?.basic || data['TrimDisposalFee']?.fixed || 0,
    defaultDeWeedingTime: data['GardenDeWeedingTime']?.basic || data['GardenDeWeedingTime']?.fixed || 120,
    defaultSoilTillingTime: data['GardenSoilTillingTime']?.basic || data['GardenSoilTillingTime']?.fixed || 20,
    defaultEdgeTime: data['GardenEdgeTime']?.basic || data['GardenEdgeTime']?.fixed || 20,
    defaultMulchPerYardPrice: data['GardenMulchPerYardPrice']?.basic || data['GardenMulchPerYardPrice']?.fixed || 0,
    defaultLawnTime: data['LawnTime']?.basic || data['LawnTime']?.fixed || config.defaultLawnTime,
    defaultSweepingTime: data['ParkingLotSweepingTime']?.basic || data['ParkingLotSweepingTime']?.fixed || 60,
    defaultSweepingDisposalFee: data['ParkingLotSweepingDFee']?.basic || data['ParkingLotSweepingDFee']?.fixed || 0,
    defaultSpringTime: data['SpringTime']?.basic || data['SpringTime']?.fixed || 180,
    defaultSpringDisposalFee: data['SpringDisposalFee']?.basic || data['SpringDisposalFee']?.fixed || 0,
    defaultFertDisposalFee: data['FertDisposalFee']?.basic || data['FertDisposalFee']?.fixed || 0,
    defaultSoilTestPrice: data['TurfSoilTestBasePrice']?.fixed || data['TurfSoilTestBasePrice']?.basic || 85,
    defaultIrrigationPrice: data['IrrigationBasePrice']?.fixed || data['IrrigationBasePrice']?.basic || 175
  });

  // Load the rates the quote is priced with: today's live prices, or a named
  // price snapshot (TKT-433). `previousRates` is set when the rep switches
  // price list: a value still equal to the previous list's default is treated
  // as untouched and follows the new list; anything the rep typed is kept.
  const loadServiceRates = async (snapshotId = null, previousRates = null) => {
    setRatesLoading(true);
    try {
      const query = snapshotId ? `?snapshotId=${encodeURIComponent(snapshotId)}` : '';
      const response = await makeApiCall(`/api/admin/service-prices/rates${query}`);
      if (response.ok) {
        const data = await response.json();
        setServiceRates(data);
        const d = rateDefaults(data);
        const o = previousRates ? rateDefaults(previousRates) : null;
        const was = (value, key) => o !== null
          && value !== '' && value !== null && value !== undefined
          && Number(value) === Number(o[key]);
        setServices(prev => {
          const snow = prev.snow;
          const getSaltPrice = (type) => type === 'JetBlue' ? d.jetBlue : d.ecoSalt;
          // Disposal fees: only fill the Admin default when the quote has no
          // value. A saved/typed $0 is a real choice (legacy quotes often
          // carry $0) and must not be bumped to the default.
          const feeOrDefault = (saved, key) =>
            (saved === '' || saved === null || saved === undefined || was(saved, key)) ? d[key] : saved;
          // Fields the form fills when empty ("x || default").
          const fillOrDefault = (saved, key) => (!saved || was(saved, key)) ? d[key] : saved;
          // Fields the form fills while they still hold the built-in default.
          const builtInOrDefault = (saved, builtIn, key) => (saved === builtIn || was(saved, key)) ? d[key] : saved;
          const mulchTotalPrice = (prev.gardening.mulchYards || 0) * d.defaultMulchPerYardPrice;
          return {
            ...prev,
            lawn: {
              ...prev.lawn,
              timeMinutes: builtInOrDefault(prev.lawn.timeMinutes, config.defaultLawnTime, 'defaultLawnTime'),
              workTimeMinutes: builtInOrDefault(prev.lawn.workTimeMinutes, config.defaultLawnTime, 'defaultLawnTime')
            },
            sweeping: {
              ...prev.sweeping,
              disposalFee: feeOrDefault(prev.sweeping.disposalFee, 'defaultSweepingDisposalFee'),
              timeMinutes: builtInOrDefault(prev.sweeping.timeMinutes, 60, 'defaultSweepingTime')
            },
            spring: {
              ...prev.spring,
              disposalFee: feeOrDefault(prev.spring.disposalFee, 'defaultSpringDisposalFee'),
              timeMinutes: builtInOrDefault(prev.spring.timeMinutes, 180, 'defaultSpringTime'),
              workTimeMinutes: builtInOrDefault(prev.spring.workTimeMinutes, 180, 'defaultSpringTime')
            },
            gardening: {
              ...prev.gardening,
              deWeedingTime: builtInOrDefault(prev.gardening.deWeedingTime, 120, 'defaultDeWeedingTime'),
              deWeedingWorkTime: builtInOrDefault(prev.gardening.deWeedingWorkTime, 120, 'defaultDeWeedingTime'),
              soilTillingTime: builtInOrDefault(prev.gardening.soilTillingTime, 20, 'defaultSoilTillingTime'),
              soilTillingWorkTime: builtInOrDefault(prev.gardening.soilTillingWorkTime, 20, 'defaultSoilTillingTime'),
              edgeTime: builtInOrDefault(prev.gardening.edgeTime, 20, 'defaultEdgeTime'),
              edgeWorkTime: builtInOrDefault(prev.gardening.edgeWorkTime, 20, 'defaultEdgeTime'),
              mulchTotalPrice
            },
            fall: {
              ...prev.fall,
              disposalFee: feeOrDefault(prev.fall.disposalFee, 'defaultFallDisposalFee'),
              timeMinutes: builtInOrDefault(prev.fall.timeMinutes, 240, 'defaultFallTimeMinutes'),
              workTimeMinutes: builtInOrDefault(prev.fall.workTimeMinutes, 240, 'defaultFallTimeMinutes')
            },
            trimming: {
              ...prev.trimming,
              applications: builtInOrDefault(prev.trimming.applications, 1, 'defaultTrimmingApplications'),
              disposalFee: feeOrDefault(prev.trimming.disposalFee, 'defaultTrimmingDisposalFee'),
              timeMinutes: builtInOrDefault(prev.trimming.timeMinutes, 120, 'defaultTrimmingTimeMinutes'),
              workTimeMinutes: builtInOrDefault(prev.trimming.workTimeMinutes, 120, 'defaultTrimmingTimeMinutes')
            },
            snow: {
              ...snow,
              parkingLotSaltingRunningTime: fillOrDefault(snow.parkingLotSaltingRunningTime, 'defaultRuns'),
              crewSaltingRunningTime: fillOrDefault(snow.crewSaltingRunningTime, 'defaultShovelCrewRunTime'),
              parkingLotPlowingRunningTime: builtInOrDefault(snow.parkingLotPlowingRunningTime, 30, 'defaultLotPlowingRunTime'),
              shovelingPlowingRunningTime: builtInOrDefault(snow.shovelingPlowingRunningTime, 60, 'defaultShovelCrewRunTime'),
              relocationPrice: fillOrDefault(snow.relocationPrice, 'defaultRelocationPrice'),
              saltingCrewCitySideWalksSaltPrice: getSaltPrice(snow.saltingCrewCitySideWalksSaltType || 'EcoSalt'),
              saltingCrewInternalSideWalksSaltPrice: getSaltPrice(snow.saltingCrewInternalSideWalksSaltType || 'EcoSalt'),
              saltingCrewDrivewaysSaltPrice: getSaltPrice(snow.saltingCrewDrivewaysSaltType || 'EcoSalt'),
              saltingCrewSqftPerMinCitySideWalks: fillOrDefault(snow.saltingCrewSqftPerMinCitySideWalks, 'defaultSqftPerMin'),
              saltingCrewSqftPerMinInternalSidewalks: fillOrDefault(snow.saltingCrewSqftPerMinInternalSidewalks, 'defaultSqftPerMin'),
              saltingCrewSqftPerMinDriveways: fillOrDefault(snow.saltingCrewSqftPerMinDriveways, 'defaultSqftPerMin')
            },
            turf: {
              ...prev.turf,
              disposalFee: feeOrDefault(prev.turf.disposalFee, 'defaultFertDisposalFee'),
              soilTestPrice: fillOrDefault(prev.turf.soilTestPrice, 'defaultSoilTestPrice'),
              irrigationBasePrice: builtInOrDefault(prev.turf.irrigationBasePrice, 175, 'defaultIrrigationPrice')
            }
          };
        });
      }
    } catch (err) {
      console.error('Failed to fetch service rates', err);
    } finally {
      setRatesLoading(false);
    }
  };

  // TKT-433: price lists (snapshots) the rep can price this quote with
  const [priceSnapshots, setPriceSnapshots] = useState([]);

  useEffect(() => {
    // An existing quote may be priced with a snapshot; load its rates.
    loadServiceRates(quote?.extra_data?.price_snapshot_id || null);
    (async () => {
      try {
        const res = await makeApiCall('/api/admin/service-prices/snapshots');
        if (res.ok) {
          const data = await res.json();
          setPriceSnapshots(data.snapshots || []);
        }
      } catch (err) {
        console.error('Failed to load price snapshots', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePriceSnapshotChange = (value) => {
    setFormData(prev => ({ ...prev, price_snapshot_id: value }));
    loadServiceRates(value || null, serviceRates);
  };
  
  // Basic quote data
  const [formData, setFormData] = useState({
    customer_id: '',
    quote_reference: '',
    description: '',
    property_type_id: '',
    sales_person_id: '',
    status: 'draft',
    auto_custom_percentage: 0,
    is_auto_custom_percentage_active: false,
    // TKT-433: '' = today's live prices, otherwise a price snapshot id
    price_snapshot_id: '',
    auto_renew: false,
    contract_date_1: '',
    contract_date_2: '',
    contract_date_3: '',
    contract_date_4: '',
    extra_conditions: ''
  });
  // Extra conditions migrated from legacy are HTML; they are shown as plain
  // text and only sent back when the rep actually edits them.
  const loadedExtraConditionsRef = useRef('');

  // Property information for calculations - defaults based on quote type
  const [propertyInfo, setPropertyInfo] = useState({
    squareFt: config.defaultPropertySize,
    parkingLotSize: config.defaultParkingLotSize,
    citySidewalks: isCommercial ? 500 : 0,
    internalSidewalks: isCommercial ? 300 : 0,
    drivewaySqFt: isCommercial ? 1000 : 0,
    gardenSqFt: config.defaultGardenSize,
    urgent_reminder: '',
    urgent_reminder_es: '',
    ...withSeasonMonths(defaultBillingSeasons())
  });
  // Until the rep edits a billing season, the defaults follow the contract start year.
  const seasonDatesTouchedRef = useRef(false);
  // Last-seen billed times for the work-time auto-sync effect below.
  const workTimeSyncBaselineRef = useRef(null);
  // Lawn "maintenance|time" the weekly mowing price was last priced from.
  const lawnPriceBaselineRef = useRef(null);

  // Service sections data - matching legacy system structure
  const [services, setServices] = useState({
    lawn: {
      enabled: false,
      maintenance: 'Basic', // Basic or Premium
      mowing: true,
      commonLawnAreaOnly: config.commonAreaDefault,
      bagging: false,
      weekPrice: 0,
      weekBasePrice: 0,
      weekPriceBagging: 0,
      weekPriceBaseBagging: 0,
      timeMinutes: config.defaultLawnTime, // Default based on quote type
      workTimeMinutes: config.defaultLawnTime, // Default to match pricing time
      startDate: '',
      endDate: '',
      truckPrice: 0,
      // Trash bags service
      trashBagServiceEnabled: false,
      trashBagType: 'OnSite', // OnSite | OffSite
      trashBagQuantity: 0,
      trashBagTimePerWeek: 0,
      trashBagPricePerBag: 0,
      trashBagExtraBagPrice: 0
    },
    gardening: {
      enabled: false,
      tariff: 'Basic', // Basic or Premium
      deWeeding: 0, // 0=none, 1=weekly, 2=biweekly
      deWeedingTime: 120,
      deWeedingWorkTime: 120, // Default to match pricing time
      soilTilling: 0, // 0=none, 1=weekly, 2=biweekly
      soilTillingTime: 20,
      soilTillingWorkTime: 20, // Default to match pricing time
      edge: 0, // 0=none, 1=weekly, 2=biweekly
      edgeTime: 20,
      edgeWorkTime: 20, // Default to match pricing time
      deWeedingWeeklyPrice: 0,
      deWeedingPriceManualOverride: false,
      soilTillingWeeklyPrice: 0,
      soilTillingPriceManualOverride: false,
      edgeWeeklyPrice: 0,
      edgePriceManualOverride: false,
      startDate: '',
      endDate: '',
      commonOnly: false,
      weedSelected: false,
      // QR-043: typed per quote and empty for a new quote, as in legacy.
      weedSprayTime: '',
      weedBaseSprayTime: '',
      mulchSelected: false,
      mulchYards: 0,
      mulchTotalPrice: 0,
      // Location selections
      frontHouseSelected: false,
      sideHouseSelected: false,
      backHouseSelected: false,
      frontCondoSelected: false,
      backCondoSelected: false,
      commonTreeRingsSelected: false
    },
    spring: {
      enabled: false,
      workSelected: false,
      // '' = not set (Admin Settings default applies); 0 = rep chose no fee.
      disposalFee: '',
      timeMinutes: 180, // Default 3 hours for spring cleanup
      workTimeMinutes: 180, // Default to match pricing time
      seasonalPrice: 0
    },
    fall: {
      enabled: false,
      selected: false,
      disposalFee: '',
      timeMinutes: 240, // Default 4 hours for fall cleanup
      workTimeMinutes: 240, // Default to match pricing time
      seasonalPrice: 0
    },
    trimming: {
      enabled: false,
      selected: false,
      applications: 1,
      timeMinutes: 120, // Default 2 hours for trimming
      disposalFee: '',
      workTimeMinutes: 120, // Default to match pricing time
      seasonalPrice: 0
    },
    turf: {
      enabled: false,
      program: 0, // 0=none, 1=simple3, 2=hybrid5, 3=premium6, 4=ultra6
      simple3Selected: false,
      simple3Price: 0,
      simple3MU: 40,
      hybrid5Selected: false,
      hybrid5Price: 0,
      hybrid5MU: 40,
      premium6Selected: false,
      premium6Price: 0,
      premium6MU: 40,
      ultra6Selected: false,
      ultra6Price: 0,
      ultra6MU: 40,
      grubControl: false,
      grubPrice: 0,
      aeration: false,
      aerationPrice: 0,
      soilTest: false,
      soilTestPrice: 0,
      // New turf addon services (Legacy parity)
      saltStopper: false,
      saltStopperPrice: 0,
      kmag: false,
      kmagPrice: 0,
      pHIncreaser: false,
      pHIncreaserPrice: 0,
      pHDecreaser: false,
      pHDecreaserPrice: 0,
      trEliteSeed: false,
      trEliteSeedPrice: 0,
      timeTopdressing: false,
      timeTopdressingPrice: 0,
      irrigation: false,
      irrigationBasePrice: 175,
      overSeedMU: 40, // Default markup for addon calculations
      disposalFee: '',
      muInitialized: false,
      sum: 0,
      baseSum: 0
    },
    snow: {
      enabled: false,
      program: 0, // 0=none, 1=basic, 2=premium
      sum: 0,
      baseSum: 0,
      // Plowing options
      parkingLotDrivewayPlowingEnabled: false,
      parkingLotDrivewayPlowingPrice: 0,
      parkingLotParkingLotPlowingEnabled: false,
      // Shoveling options
      shovelingCitySidewalkEnabled: false,
      shovelingInternalSidewalkEnabled: false,
      shovelingDrivewayEnabled: false,
      shovelingCitySidewalkPrice: '',
      shovelingInternalSidewalkPrice: '',
      shovelingDrivewayPrice: '',
      // Salting options (basic toggles for simple UI)
      citySaltingEnabled: false,
      internalSaltingEnabled: false,
      drivewaySaltingEnabled: false,
      parkingPlowingEnabled: false,
      parkingSaltingEnabled: false,
      // Truck salting (Legacy)
      truckParkingLotSaltingEnabled: false,
      truckParkingLotSaltingPrice: '',
      truckDrivewaySaltingEnabled: false,
      truckDrivewaySaltingPrice: 0,
      // TKT-294: a season covers ~30 truck runs and ~60 crew runs.
      parkingLotSaltingRunningTime: 30,
      // Crew salting (Legacy)
      crewCitySidewalkSaltingEnabled: false,
      crewInternalSidewalkSaltingEnabled: false,
      crewDrivewaySaltingEnabled: false,
      crewSaltingRunningTime: 60,
      crewSaltingSaltBins: 0,
      saltingCrewCitySideWalksSaltPrice: 50,
      saltingCrewInternalSideWalksSaltPrice: 50,
      saltingCrewDrivewaysSaltPrice: 50,
      saltingCrewCitySideWalksSaltType: 'EcoSalt',
      saltingCrewInternalSideWalksSaltType: 'EcoSalt',
      saltingCrewDrivewaysSaltType: 'EcoSalt',
      // TKT-295: manual price override for crew driveway salting.
      saltingCrewDrivewaysPrice: '',
      saltingCrewSqftPerMinCitySideWalks: 65,
      saltingCrewSqftPerMinInternalSidewalks: 65,
      saltingCrewSqftPerMinDriveways: 65,
      // Tariffs (1=Easy, 2=Medium, 3=Hard)
      parkingLotPlowingTariff: 1,
      shovelingCitySidewalkTariff: 1,
      shovelingInternalSidewalkTariff: 1,
      shovelingDrivewayTariff: 1,
      parkingLotPlowingRunningTime: 30,
      shovelingPlowingRunningTime: 60,
      // Snow extras (Legacy parity)
      trashBagEnabled: false,
      trashBagType: 'OnSite',
      trashBagQuantity: 0,
      trashBagTimePerWeek: 0,
      // TKT-297: manual price override for the snow trash bag service.
      trashBagPrice: '',
      saltBinsEnabled: false,
      saltBins: 0,
      saltBinsFinalPrice: null,
      premiumSaltingEnabled: false,
      premiumSaltingPrice: 0,
      perEventEnabled: false,
      perEventPrice: 0,
      isCustomPerEventPrice: false,
      parkingLotDrivewayPlowingBasePrice: 0,
      premiumSaltingBasePrice: 0,
      allInclusive: false,
      relocationPrice: 0,
      // Work time for operational scheduling (separate per service type, matching Legacy)
      plowingWorkTimeMinutes: 0,
      shovelingWorkTimeMinutes: 0,
      saltingWorkTimeMinutes: 0,
      residentialWorkTimeMinutes: 0
    },
    // Sweeping service (residential + commercial)
    sweeping: {
      enabled: false,
      timeMinutes: 60,
      disposalFee: ''
    },
    // Annuals/Purchasing service (Legacy parity)
    annuals: {
      enabled: false,
      purchasePlantingFlats: false,
      purchasePlantingFlatsFee: 0,
      purchasePlantingFlatsBaseFee: 0,
      purchasePlantingPots: false,
      purchasePlantingPotsFee: 0,
      purchasePlantingPotsBaseFee: 0
    },
    // Property inspection service (Legacy parity)
    inspection: {
      enabled: false,
      time1: 0,
      time2: 0,
      time3: 0,
      defaultPricing: 0
    },
    // Commercial-specific services
    parkingLot: isCommercial ? {
      enabled: false,
      sweeping: false,
      sweepingFrequency: 'Weekly',
      sweepingPrice: 0,
      lineStriping: false,
      lineStripingPrice: 0,
      crackFilling: false,
      crackFillingPrice: 0,
      sealCoating: false,
      sealCoatingPrice: 0
    } : null
  });

  // Commercial-specific billing information
  const [billingInfo, setbillingInfo] = useState(isCommercial ? {
    arContact: '',
    arPhone: '',
    arEmail: '',
    arName: '',
    paymentTerms: 'NET30',
    invoiceFrequency: 'Monthly'
  } : {
    arContact: '',
    arPhone: '',
    arEmail: '',
    arName: '',
    paymentTerms: 'NET30',
    invoiceFrequency: 'Monthly'
  });

  const selectedPropertyTypeName = (lookups.propertyTypes || [])
    .find((pt) => String(pt.id) === String(formData.property_type_id))?.type_name || '';
  const multiOrIndustrialSnow = /multi|industrial/.test(
    `${selectedPropertyTypeName} ${selectedCustomer?.customer_type || ''}`.toLowerCase()
  );
  const showResidentialSnowPackages = isConstruction || (isResidential && !multiOrIndustrialSnow);
  const showCommercialSnowSections = isConstruction || isCommercial || multiOrIndustrialSnow;
  const pricedServices = useMemo(
    () => applySnowVisibility(services, {
      construction: isConstruction,
      residentialPackages: showResidentialSnowPackages,
      commercialSections: showCommercialSnowSections,
    }),
    [services, isConstruction, showResidentialSnowPackages, showCommercialSnowSections]
  );

  // Use the backend pricing calculation hook
  const { pricing, loading: pricingLoading, error: pricingError, recalculate } = usePricing(
    pricedServices, 
    propertyInfo,
    { 
      percentage: formData.auto_custom_percentage, 
      isActive: formData.is_auto_custom_percentage_active 
    },
    true,
    formData.price_snapshot_id || null
  );

  // TKT-207: align accordion order with Paramount's preferred legacy
  // service order: Contact -> Inspection -> Spring -> Sweeping -> Lawn
  // -> Gardening -> Annuals -> Trimming -> Fall -> Turf -> Snow.
  // Property + parkingLot stay alongside their natural neighbours.
  const serviceSections = [
    {
      key: 'customer',
      title: 'Customer Information',
      icon: <ContactIcon />,
      color: config.color,
      required: true
    },
    {
      key: 'property',
      title: 'Property Information',
      icon: isCommercial ? <BusinessIcon /> : <LawnIcon />,
      color: '#1976d2',
      description: isCommercial ? 'Commercial property dimensions and details' : 'Property dimensions for accurate calculations'
    },
    {
      key: 'inspection',
      title: t('quotes.inspection.title', 'Property Inspection'),
      icon: <InspectionIcon />,
      color: '#5c6bc0',
      description: t('quotes.inspection.description', 'Property inspection timing and pricing')
    },
    {
      key: 'spring',
      title: 'Spring Cleanup',
      icon: <SpringIcon />,
      color: '#ffeb3b',
      description: 'Seasonal spring cleanup services'
    },
    {
      key: 'sweeping',
      title: 'Parking Lot Sweeping',
      icon: <ParkingLotIcon />,
      color: '#78909c',
      description: 'Parking lot and driveway sweeping services'
    },
    {
      key: 'lawn',
      title: isCommercial ? 'Lawn Maintenance' : 'Lawn Care',
      icon: <LawnIcon />,
      color: '#4caf50',
      description: isCommercial ? 'Commercial lawn maintenance and care' : 'Weekly lawn maintenance and care'
    },
    ...(isCommercial ? [{
      key: 'parkingLot',
      title: 'Parking Lot Services',
      icon: <ParkingLotIcon />,
      color: '#607d8b',
      description: 'Parking lot sweeping, striping, and maintenance'
    }] : []),
    {
      key: 'gardening',
      title: isCommercial ? 'Garden Maintenance' : 'Gardening Services',
      icon: <GardeningIcon />,
      color: '#e91e63',
      description: isCommercial ? 'Commercial landscaping and garden care' : 'Garden maintenance, weeding, and care'
    },
    {
      key: 'annuals',
      title: 'Annuals & Purchasing',
      icon: <GardeningIcon />,
      color: '#ec407a',
      description: 'Annual flowers, flats, and pots planting'
    },
    {
      key: 'trimming',
      title: 'Trimming & Pruning',
      icon: <TrimmingIcon />,
      color: '#795548',
      description: isCommercial ? 'Commercial landscaping trimming services' : 'Shrub and tree trimming services'
    },
    {
      key: 'fall',
      title: 'Fall Cleanup',
      icon: <FallIcon />,
      color: '#ff9800',
      description: 'Seasonal fall cleanup and leaf removal'
    },
    {
      key: 'turf',
      title: 'Turf Programs',
      icon: <TurfIcon />,
      color: '#2e7d32',
      description: isCommercial ? 'Commercial fertilization and turf care' : 'Fertilization, grub control, and aeration'
    },
    {
      key: 'snow',
      title: 'Snow Removal',
      icon: <SnowIcon />,
      color: '#2196f3',
      description: isCommercial ? 'Commercial snow removal and ice management' : 'Winter snow removal and salting'
    },
    ...(isCommercial ? [{
      key: 'billing',
      title: 'Billing Information',
      icon: <ContactIcon />,
      color: '#9c27b0',
      description: 'Accounts receivable and billing contacts'
    }] : [])
  ];

  // Customer search with debouncing. Server results are shown as returned:
  // the autocomplete must not filter them again by name, or an address
  // search drops every match. A newer keystroke invalidates the older request.
  const customerSearchRequest = useRef(0);
  useEffect(() => {
    if (!customerSearchTerm || customerSearchTerm.length < 2) {
      setCustomerOptions([]);
      return;
    }

    const requestId = ++customerSearchRequest.current;
    const timeoutId = setTimeout(async () => {
      setCustomerLoading(true);
      try {
        const res = await makeApiCall(`api/customers?search=${encodeURIComponent(customerSearchTerm)}&limit=50`);
        const data = await res.json();
        if (requestId !== customerSearchRequest.current) return;
        if (res.ok) {
          const customers = data.customers || data.data || [];
          setCustomerOptions(customers);
        }
      } catch (e) {
        if (requestId !== customerSearchRequest.current) return;
        console.error('Failed to search customers:', e);
      } finally {
        if (requestId === customerSearchRequest.current) setCustomerLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      customerSearchRequest.current += 1;
    };
  }, [customerSearchTerm]);

  // Initialize form
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const lookupRes = await makeApiCall('api/quotes/lookup');
        const lookupData = await lookupRes.json();
        if (!lookupRes.ok) throw new Error(lookupData.error || 'Failed to load lookups');
        setLookups(lookupData);

        // TKT-566: a new quote starts with whoever is creating it as the sales
        // rep who signs the contract (they can still change it). Someone who
        // isn't a sales person leaves it on the company default signer.
        if (!quote) {
          const mine = (lookupData.salesPersons || []).find((sp) => user?.id && sp.user_id === user.id);
          if (mine) {
            setFormData((prev) => (prev.sales_person_id ? prev : { ...prev, sales_person_id: mine.id }));
          }
        }

        if (quote) {
          if (quote.is_auto_custom_percentage_active) {
            setAdjustmentDraft(String(quote.auto_custom_percentage ?? ''));
          }
          loadAdjustmentHistory(quote.id);
          setFormData({
            customer_id: quote.customer_id || '',
            quote_reference: quote.quote_reference || '',
            description: quote.description || '',
            property_type_id: quote.property_type_id || '',
            sales_person_id: quote.sales_person_id || '',
            status: quote.status || 'draft',
            auto_custom_percentage: quote.auto_custom_percentage || 0,
            is_auto_custom_percentage_active: quote.is_auto_custom_percentage_active || false,
            price_snapshot_id: quote.extra_data?.price_snapshot_id || '',
            auto_renew: quote.extra_data?.auto_renew || false,
            contract_date_1: quote.extra_data?.contract_date_1 || '',
            contract_date_2: quote.extra_data?.contract_date_2 || '',
            contract_date_3: quote.extra_data?.contract_date_3 || '',
            contract_date_4: quote.extra_data?.contract_date_4 || '',
            extra_conditions: conditionsToText(quote.extra_data?.extra_conditions)
          });
          loadedExtraConditionsRef.current = conditionsToText(quote.extra_data?.extra_conditions);
          seasonDatesTouchedRef.current = SEASON_DATE_KEYS.some(key => quote.property_info?.[key]);
          
          // If editing, load the existing customer and service configurations
          if (quote.customer_id) {
            try {
              const customerRes = await makeApiCall(`api/customers/${quote.customer_id}`);
              const customerData = await customerRes.json();
              if (customerRes.ok) {
                setSelectedCustomer(customerData);
              }
            } catch (e) {
              console.error('Failed to load customer:', e);
            }
          }

          // Load service configurations if they exist
          if (quote.service_configs && Array.isArray(quote.service_configs)) {
            const loadedServices = { ...services };
            quote.service_configs.forEach(config => {
              if (config.service_type && config.config_data) {
                const data = { ...config.config_data };
                // Backward compat: migrate old single workTimeMinutes to separate fields
                if (config.service_type === 'snow' && data.workTimeMinutes != null && data.plowingWorkTimeMinutes == null) {
                  data.plowingWorkTimeMinutes = data.workTimeMinutes;
                  data.shovelingWorkTimeMinutes = 0;
                  data.saltingWorkTimeMinutes = 0;
                  data.residentialWorkTimeMinutes = 0;
                  delete data.workTimeMinutes;
                }
                loadedServices[config.service_type] = {
                  ...loadedServices[config.service_type],
                  ...data,
                  enabled: true
                };
              }
            });
            workTimeSyncBaselineRef.current = workTimeSyncSnapshot(loadedServices);
            // QR-029: keep the saved weekly lawn price (it used to be
            // recalculated from a hardcoded rate every time a quote opened,
            // silently dropping negotiated prices).
            lawnPriceBaselineRef.current = loadedServices.lawn
              ? `${loadedServices.lawn.maintenance}|${loadedServices.lawn.timeMinutes}`
              : null;
            setServices(loadedServices);

            // Expand sections that have enabled services
            const enabledSections = quote.service_configs
              .map(config => config.service_type)
              .filter(type => type !== 'customer');
            setExpandedSections(prev => [...new Set([...prev, ...enabledSections])]);
          }

          // Load property information if it exists
          if (quote.property_info) {
            const propInfo = quote.property_info;
            
            // Extract billing_info if it exists (for commercial quotes)
            if (propInfo.billing_info && isCommercial) {
              setbillingInfo(propInfo.billing_info);
              // Remove billing_info from property_info to avoid duplication
              const { billing_info, ...cleanPropertyInfo } = propInfo;
              setPropertyInfo(normalizeLoadedPropertyInfo(cleanPropertyInfo, quote.extra_data?.contract_date_1));
            } else {
              setPropertyInfo(normalizeLoadedPropertyInfo(propInfo, quote.extra_data?.contract_date_1));
            }
          } else {
            setPropertyInfo(prev => ({
              ...prev,
              ...withSeasonMonths(defaultBillingSeasons(quote.extra_data?.contract_date_1))
            }));
          }
        }
      } catch (e) {
        setError('Failed to load metadata');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [quote]);

  const numVal = (v, float = false) => {
    if (v === '') return '';
    const n = float ? parseFloat(v) : parseInt(v, 10);
    return isNaN(n) ? '' : n;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const loadAdjustmentHistory = async (quoteId) => {
    if (!quoteId) return;
    try {
      const res = await makeApiCall(`api/quotes/${quoteId}/price-adjustment-history`);
      const data = await res.json();
      if (res.ok) setAdjustmentHistory(data.history || []);
    } catch (e) {
      console.error('Failed to load price adjustment history', e);
    }
  };

  const applyPriceAdjustment = async () => {
    const pct = parseFloat(adjustmentDraft);
    if (Number.isNaN(pct)) {
      setError(t('quoteForm.renewal.invalidPercentage', 'Enter a percentage.'));
      return;
    }
    setError('');
    setFormData((prev) => ({
      ...prev,
      auto_custom_percentage: pct,
      is_auto_custom_percentage_active: true,
    }));
    const id = quote?.id || createdQuoteId;
    if (!id) {
      pendingAdjustmentRef.current = { adjustment_type: 'update', percentage: pct };
      return;
    }
    setAdjustmentSaving(true);
    try {
      const res = await makeApiCall(`api/quotes/${id}/price-adjustment`, {
        method: 'POST',
        body: JSON.stringify({ percentage: pct }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('quoteForm.renewal.saveFailed', 'Could not save the price adjustment.'));
      setAdjustmentHistory(data.history || []);
      pendingAdjustmentRef.current = null;
    } catch (e) {
      pendingAdjustmentRef.current = { adjustment_type: 'update', percentage: pct };
      setError(e.message);
    } finally {
      setAdjustmentSaving(false);
    }
  };

  const resetPriceAdjustment = async () => {
    setError('');
    setAdjustmentDraft('');
    setFormData((prev) => ({
      ...prev,
      auto_custom_percentage: 0,
      is_auto_custom_percentage_active: false,
    }));
    const id = quote?.id || createdQuoteId;
    if (!id) {
      pendingAdjustmentRef.current = { adjustment_type: 'reset', percentage: 0 };
      return;
    }
    setAdjustmentSaving(true);
    try {
      const res = await makeApiCall(`api/quotes/${id}/price-adjustment/reset`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('quoteForm.renewal.saveFailed', 'Could not save the price adjustment.'));
      setAdjustmentHistory(data.history || []);
      pendingAdjustmentRef.current = null;
    } catch (e) {
      pendingAdjustmentRef.current = { adjustment_type: 'reset', percentage: 0 };
      setError(e.message);
    } finally {
      setAdjustmentSaving(false);
    }
  };

  const handlePendingSiteDiagram = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('quotes.siteDiagramInvalidType'));
      event.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t('quotes.siteDiagramTooLarge'));
      event.target.value = '';
      return;
    }

    setPendingSiteDiagram(file);
    setPendingSiteDiagramPreview(URL.createObjectURL(file));
    setError('');
  };

  const normalizeLoadedPropertyInfo = (info = {}, contractStart = '') => {
    const defaults = defaultBillingSeasons(contractStart);
    const seasons = {};
    SEASON_DATE_KEYS.forEach(key => {
      seasons[key] = info[key] || defaults[key];
    });
    return withSeasonMonths({
      ...info,
      ...seasons,
      urgent_reminder: info.urgent_reminder || info.urgentReminder || info.UrgentReminder || '',
      urgent_reminder_es: info.urgent_reminder_es || ''
    });
  };

  const handleSeasonDateChange = (season, edge, value) => {
    seasonDatesTouchedRef.current = true;
    const key = `${season}${edge === 'start' ? 'StartDate' : 'EndDate'}`;
    setPropertyInfo(prev => withSeasonMonths({ ...prev, [key]: value }));
  };

  const resetBillingSeasons = () => {
    seasonDatesTouchedRef.current = false;
    setPropertyInfo(prev => ({ ...prev, ...withSeasonMonths(defaultBillingSeasons(formData.contract_date_1)) }));
  };

  useEffect(() => {
    if (seasonDatesTouchedRef.current) return;
    setPropertyInfo(prev => ({ ...prev, ...withSeasonMonths(defaultBillingSeasons(formData.contract_date_1)) }));
  }, [formData.contract_date_1]);

  const translateReminderToSpanish = async () => {
    const text = (propertyInfo.urgent_reminder || '').trim();
    if (!text) return;

    try {
      setTranslatingReminder(true);
      setReminderTranslateError('');
      const response = await makeApiCall('/api/translation/to-es-mx', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('quotes.inspection.failedToTranslateReminder'));
      }

      // TKT-282: write the suggestion into the Spanish-only field, never
      // into urgent_reminder — overwriting English is why English-logged-in
      // crews previously saw Spanish text.
      setPropertyInfo((prev) => ({
        ...prev,
        urgent_reminder_es: data.translatedText || text,
      }));
    } catch (err) {
      console.error(err);
      setReminderTranslateError(
        err.message || t('quotes.inspection.failedToTranslateReminder')
      );
    } finally {
      setTranslatingReminder(false);
    }
  };

  const handleBillingInfoChange = (field, value) => {
    setbillingInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceChange = (section, field, value) => {
    setServices(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleSiteDiagramValues = (values) => {
    // Update property info with calculated values from site diagram
    if (values.breakdown) {
      // Calculate ONLY turf/lawn area for the main squareFt property
      // We explicitly exclude non-turf areas to match legacy behavior where "SquareFt" = Lawn Area
      const lawnArea = values.breakdown.lawn?.squareFeet || 0;
      
      setPropertyInfo(prev => ({
        ...prev,
        // squareFt should ONLY be the lawn area, not the total drawn area
        squareFt: lawnArea || prev.squareFt,
        drivewaySqFt: values.breakdown.driveway?.squareFeet || prev.drivewaySqFt,
        citySidewalks: values.breakdown.cityWalkway?.squareFeet || values.breakdown.sidewalk?.squareFeet || prev.citySidewalks,
        internalSidewalks: values.breakdown.internalWalkway?.squareFeet || values.breakdown.sidewalk?.squareFeet || prev.internalSidewalks,
        gardenSqFt: values.breakdown.garden?.squareFeet || prev.gardenSqFt
      }));
    }
  };

  // Special handler for turf programs to maintain mutual exclusivity and update program field
  const handleTurfProgramChange = (programType, checked) => {
    setServices(prev => {
      const newTurf = { ...prev.turf };
      
      if (checked) {
        // Clear all program selections first
        newTurf.simple3Selected = false;
        newTurf.hybrid5Selected = false;
        newTurf.premium6Selected = false;
        newTurf.ultra6Selected = false;
        
        // Set the selected program and update the program field
        switch (programType) {
          case 'simple3':
            newTurf.simple3Selected = true;
            newTurf.program = 1; // 1=simple3
            break;
          case 'hybrid5':
            newTurf.hybrid5Selected = true;
            newTurf.program = 2; // 2=hybrid5
            break;
          case 'premium6':
            newTurf.premium6Selected = true;
            newTurf.program = 3; // 3=premium6
            break;
          case 'ultra6':
            newTurf.ultra6Selected = true;
            newTurf.program = 4; // 4=ultra6
            break;
        }
      } else {
        // If unchecking, clear the specific program and set program to 0 (none)
        switch (programType) {
          case 'simple3':
            newTurf.simple3Selected = false;
            break;
          case 'hybrid5':
            newTurf.hybrid5Selected = false;
            break;
          case 'premium6':
            newTurf.premium6Selected = false;
            break;
          case 'ultra6':
            newTurf.ultra6Selected = false;
            break;
        }
        newTurf.program = 0; // 0=none
      }
      
      return {
        ...prev,
        turf: newTurf
      };
    });
  };

  const handleSnowProgramChange = (program, checked) => {
    setServices(prev => {
      const selectedProgram = checked ? program : 0;
      const rateAlias = {
        1: 'ResidentialSnowBasicSeasonalPrice',
        2: 'ResidentialSnowPremiumSeasonalPrice',
        3: 'ResidentialSnowPremiumSaltSeasonalPrice',
      }[selectedProgram];
      const defaultBasePrice = rateAlias
        ? (serviceRates[rateAlias]?.basic || serviceRates[rateAlias]?.fixed || 0)
        : 0;

      return {
        ...prev,
        snow: {
          ...prev.snow,
          program: selectedProgram,
          baseSum: defaultBasePrice > 0 ? defaultBasePrice : prev.snow.baseSum
        }
      };
    });
  };

  // Auto-calculate turf pricing when enabled, program selection, or property size changes
  useEffect(() => {
    const calculateTurfPricingAutomatically = async () => {
      if (!services.turf.enabled) {
        setTurfPricing({ total: 0, breakdown: {}, programName: 'None' });
        return;
      }

      setTurfPricingLoading(true);
      try {
        const overSeedMU = services.turf.overSeedMU;
        const needsFreshBase = !turfBasePrices
          || propertyInfo.squareFt !== turfBasePrices.lastSquareFt
          || overSeedMU !== turfBasePrices.lastOverSeedMU;

        // Get base prices for the current property size / overseed MU
        if (needsFreshBase) {
          const basePrices = await getBasePricesForSquareFt(propertyInfo.squareFt || 1000, overSeedMU);
          basePrices.lastSquareFt = propertyInfo.squareFt || 1000;
          basePrices.lastOverSeedMU = overSeedMU;
          // TKT-227 / TKT-292: NEW quotes and older quotes (where MU boxes
          // were never shown) seed markup % from admin settings. After
          // muInitialized, the quote keeps the values the user typed.
          if (!services.turf.muInitialized && basePrices.settings) {
            setServices(prev => {
              if (prev.turf.muInitialized) return prev;
              const parseMU = (name, fallback) => {
                const value = parseFloat(basePrices.settings[name]);
                return isNaN(value) ? fallback : value;
              };
              return {
                ...prev,
                turf: {
                  ...prev.turf,
                  simple3MU: parseMU('simple_3_price', prev.turf.simple3MU),
                  hybrid5MU: parseMU('hybrid_5_price', prev.turf.hybrid5MU),
                  premium6MU: parseMU('premium_6_price', prev.turf.premium6MU),
                  ultra6MU: parseMU('ultra_6_price', prev.turf.ultra6MU),
                  overSeedMU: parseMU('overseed_price', prev.turf.overSeedMU),
                  muInitialized: true
                }
              };
            });
          }
          setTurfBasePrices(basePrices);
        }

        // Calculate total pricing
        const pricing = await calculateTotalTurfPricing(services, propertyInfo);
        setTurfPricing(pricing);

        // Update services sum to match calculated program/other-suggested total.
        // Specialty addons are added by the backend pricing endpoint.
        setServices(prev => ({
          ...prev,
          turf: {
            ...prev.turf,
            sum: pricing.total,
            baseSum: pricing.total,
            // QR-070: each line's own (unadjusted) price, so the PDF can list
            // the program, grub control, aeration and soil test separately
            // instead of printing the whole sum on the program line.
            programPrice: pricing.breakdown?.program?.price || 0,
            grubControlPrice: pricing.breakdown?.grubControl?.price || 0,
            aerationLinePrice: pricing.breakdown?.aeration?.price || 0,
            soilTestLinePrice: pricing.breakdown?.soilTest?.price || 0
          }
        }));

      } catch (error) {
        console.error('Error calculating turf pricing:', error);
        // Set fallback pricing if database tables don't exist yet
        setTurfPricing({ total: 0, breakdown: {}, programName: 'None' });
        setTurfBasePrices({ turf: 0, aeration: 0, grub: 0, settings: {}, addons: {} });
      } finally {
        setTurfPricingLoading(false);
      }
    };

    calculateTurfPricingAutomatically();
  }, [
    services.turf.enabled,
    services.turf.program,
    services.turf.aeration,
    services.turf.grubControl,
    services.turf.soilTest,
    services.turf.soilTestPrice,
    services.turf.grubPrice,
    services.turf.aerationPrice,
    services.turf.simple3MU,
    services.turf.hybrid5MU,
    services.turf.premium6MU,
    services.turf.ultra6MU,
    services.turf.overSeedMU,
    services.turf.muInitialized,
    services.turf.saltStopper,
    services.turf.kmag,
    services.turf.pHIncreaser,
    services.turf.pHDecreaser,
    services.turf.trEliteSeed,
    services.turf.timeTopdressing,
    services.turf.irrigation,
    services.turf.saltStopperPrice,
    services.turf.kmagPrice,
    services.turf.pHIncreaserPrice,
    services.turf.pHDecreaserPrice,
    services.turf.trEliteSeedPrice,
    services.turf.timeTopdressingPrice,
    services.turf.irrigationBasePrice,
    services.turf.disposalFee,
    propertyInfo.squareFt
  ]);

  // Auto-calculate the weekly mowing price: Admin Settings "Lawn" rate
  // (Basic/Premium) / 60 x Time (mins), as legacy did (QR-026; it used to
  // use a hardcoded $130/$160). TKT-179: a manually typed price is left
  // alone until the rep clicks "Reset to auto".
  // QR-029: a saved quote keeps its weekly price when it's opened. Only a
  // change to maintenance level or time (or an empty price) reprices it;
  // the baseline is set when a quote loads and cleared by "Reset to auto".
  // QR-030: bagging is a typed price in legacy, never derived from mowing.
  useEffect(() => {
    if (!services.lawn.enabled) return;
    const current = `${services.lawn.maintenance}|${services.lawn.timeMinutes}`;
    const baseline = lawnPriceBaselineRef.current;
    lawnPriceBaselineRef.current = current;
    if (services.lawn.weekPriceManualOverride) return;
    if (baseline === current && Number(services.lawn.weekPrice) > 0) return;

    const lawnRate = serviceRates.Lawn || {};
    const hourlyRate = services.lawn.maintenance === 'Premium'
      ? (lawnRate.premium || lawnRate.basic)
      : lawnRate.basic;
    if (!hourlyRate) return; // Admin rates not loaded yet; runs again when they are.

    const weekly = Math.round(((hourlyRate / 60) * (Number(services.lawn.timeMinutes) || 0)) * 100) / 100;
    setServices(prev => ({
      ...prev,
      lawn: { ...prev.lawn, weekPrice: weekly, weekBasePrice: weekly }
    }));
  }, [services.lawn.maintenance, services.lawn.timeMinutes, services.lawn.enabled, services.lawn.weekPriceManualOverride, services.lawn.weekPrice, serviceRates]);

  // Auto-sync a crew work time to its billed (pricing) time when the user
  // edits that billed time and the work time is still 0. Only fields whose
  // billed time actually changed are synced, and loading a saved quote resets
  // the baseline first: otherwise a work time deliberately set to 0 (e.g.
  // edging the client doesn't get) was refilled on every reopen and saved
  // back, inflating the crew's allotted time.
  useEffect(() => {
    const current = workTimeSyncSnapshot(services);
    const baseline = workTimeSyncBaselineRef.current;
    workTimeSyncBaselineRef.current = current;
    if (!baseline) return;
    const changed = WORK_TIME_SYNC_FIELDS.filter(([section, pricingField]) => (
      baseline[`${section}.${pricingField}`] !== current[`${section}.${pricingField}`]
    ));
    if (changed.length === 0) return;
    setServices(prev => {
      const updated = { ...prev };
      changed.forEach(([section, pricingField, workField]) => {
        const pricing = prev[section]?.[pricingField];
        if (pricing && prev[section][workField] === 0) {
          updated[section] = { ...updated[section], [workField]: pricing };
        }
      });
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services.lawn.timeMinutes, services.spring.timeMinutes, services.fall.timeMinutes, services.trimming.timeMinutes, services.gardening.deWeedingTime, services.gardening.soilTillingTime, services.gardening.edgeTime]);

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const calculateTotal = () => {
    // Use backend-calculated total
    return pricing.grandTotal || 0;
  };

  const getGardeningHourlyRate = (gardening = services.gardening) => {
    const basic = serviceRates.gardening?.basic || 130;
    const premium = serviceRates.gardening?.premium || basic;
    return gardening.tariff === 'Premium' ? premium : basic;
  };

  const calculateGardeningLineWeekly = (frequency, minutes, gardening = services.gardening) => {
    if (!frequency || frequency <= 0) return 0;
    const perMinute = getGardeningHourlyRate(gardening) / 60;
    const base = perMinute * (Number(minutes) || 0);
    return frequency === 2 ? base / 2 : base;
  };

  const calculateGardeningLineSeasonal = (frequency, minutes, gardening = services.gardening) => {
    const weekly = calculateGardeningLineWeekly(frequency, minutes, gardening);
    const weeks = calcWeeks(gardening.startDate, gardening.endDate);
    return weekly * weeks;
  };

  const handleGardeningMulchYardsChange = (yards) => {
    const mulchPerYard = serviceRates['GardenMulchPerYardPrice']?.basic || serviceRates['GardenMulchPerYardPrice']?.fixed || 0;
    setServices(prev => ({
      ...prev,
      gardening: {
        ...prev.gardening,
        mulchYards: yards,
        mulchTotalPrice: yards * mulchPerYard
      }
    }));
  };

  const onSubmit = async () => {
    setError('');
    if (!selectedCustomer || !formData.customer_id) {
      setError('Please select a customer');
      return;
    }
    
    setLoading(true);
    try {
      // The `pricing` from usePricing is debounced (500ms + round trip), so it
      // can lag the form: change "Run Times" and click Save straight away and
      // the previous total gets stored while the config stores the new value
      // (Q-202608-0020 saved $6,972.12 for a $6,672.12 snow config that way).
      // Price the exact state being saved instead, falling back to the
      // debounced value only if the request fails.
      let savePricing = pricing;
      try {
        const pricingRes = await makeApiCall('/api/pricing/calculate', {
          method: 'POST',
          body: JSON.stringify({
            services: pricedServices,
            propertyInfo,
            markup: {
              percentage: formData.auto_custom_percentage,
              isActive: formData.is_auto_custom_percentage_active
            },
            priceSnapshotId: formData.price_snapshot_id || null
          })
        });
        if (pricingRes.ok) {
          savePricing = await pricingRes.json();
        }
      } catch (pricingErr) {
        console.error('Fresh pricing before save failed; using last debounced pricing', pricingErr);
      }

      // Convert services to line items and service configs
      const items = [];
      const serviceConfigs = [];
      
      // First, calculate and store totals in each service object before processing
      const updatedServices = { ...pricedServices };
      
      // Use backend-calculated totals for service sums
      if (updatedServices.gardening.enabled) {
        updatedServices.gardening.sum = savePricing.gardening?.seasonalTotal || 0;
      }
      // For spring/fall/trimming `sum` is the rep's "Manual Seasonal Price
      // Override" (TKT-179) and the backend returns it verbatim when > 0.
      // Writing the calculated total into it here locked every saved quote
      // to its first total (later time/disposal edits were ignored). The
      // calculated total goes to `seasonalPrice` (what the PDF reads) and
      // `sum` is left as whatever the rep typed.
      if (updatedServices.spring.enabled) {
        updatedServices.spring.seasonalPrice = savePricing.spring?.total || 0;
      }
      if (updatedServices.fall.enabled) {
        updatedServices.fall.seasonalPrice = savePricing.fall?.total || 0;
      }
      if (updatedServices.trimming.enabled) {
        updatedServices.trimming.seasonalPrice = savePricing.trimming?.total || 0;
      }
      if (updatedServices.turf.enabled) {
        // turf.sum is the program base the backend builds on (it adds
        // specialty addons + disposal fee on top). Saving the backend total
        // back into it added the disposal fee again on every save
        // (Q-202609-0017: 666.66 -> 716.66 -> 766.66).
        updatedServices.turf.seasonalPrice = savePricing.turf?.total || 0;
      }
      if (updatedServices.snow.enabled) {
        updatedServices.snow.sum = savePricing.snow?.total || 0;
        // Don't overwrite user-entered baseSum (residential snow base price)
        // Only set baseSum from backend if user hasn't entered one
        if (!updatedServices.snow.baseSum || updatedServices.snow.baseSum <= 0) {
          updatedServices.snow.baseSum = savePricing.snow?.baseSum || 0;
        }
      }
      if (updatedServices.lawn.enabled) {
        // Update lawn pricing from backend calculations
        updatedServices.lawn.weekPrice = savePricing.lawn?.weeklyPrice || 0;
        updatedServices.lawn.weekPriceBagging = savePricing.lawn?.baggingWeekly || 0;
      }
      
      Object.keys(updatedServices).forEach(key => {
        if (updatedServices[key] && updatedServices[key].enabled) {
          const service = updatedServices[key];
          const section = serviceSections.find(s => s.key === key);
          
          // Create line items based on service type
          if (key === 'gardening') {
            // Add line items for each enabled gardening service
            const gardenWeeks = calcWeeks(service.startDate, service.endDate);
            const agreedSeasonal = (frequency, minutes, flag, weeklyField) => {
              const weekly = service[flag]
                ? (Number(service[weeklyField]) || 0)
                : calculateGardeningLineWeekly(frequency, minutes, service);
              return Math.round(weekly * gardenWeeks * 100) / 100;
            };
            if (service.deWeeding > 0) {
              items.push({
                description: `De-weeding (${service.deWeeding === 1 ? 'Weekly' : 'Bi-weekly'})`,
                quantity: 1,
                unit_price: agreedSeasonal(service.deWeeding, service.deWeedingTime, 'deWeedingPriceManualOverride', 'deWeedingWeeklyPrice'),
                discount_percent: 0,
                item_type: 'service',
                service_code: 'gardening_deweeding'
              });
            }
            if (service.soilTilling > 0) {
              items.push({
                description: `Soil Tilling (${service.soilTilling === 1 ? 'Weekly' : 'Bi-weekly'})`,
                quantity: 1,
                unit_price: agreedSeasonal(service.soilTilling, service.soilTillingTime, 'soilTillingPriceManualOverride', 'soilTillingWeeklyPrice'),
                discount_percent: 0,
                item_type: 'service',
                service_code: 'gardening_soiltilling'
              });
            }
            if (service.edge > 0) {
              items.push({
                description: `Edge Maintenance (${service.edge === 1 ? 'Weekly' : 'Bi-weekly'})`,
                quantity: 1,
                unit_price: agreedSeasonal(service.edge, service.edgeTime, 'edgePriceManualOverride', 'edgeWeeklyPrice'),
                discount_percent: 0,
                item_type: 'service',
                service_code: 'gardening_edge'
              });
            }
            if (service.weedSelected) {
              items.push({
                description: 'Weed Spray Application',
                quantity: 1,
                unit_price: service.weedSprayTime,
                discount_percent: 0,
                item_type: 'service',
                service_code: 'gardening_weedspray'
              });
            }
            if (service.mulchSelected) {
              items.push({
                description: `Mulch Service (${service.mulchYards} yards)`,
                quantity: service.mulchYards,
                unit_price: service.mulchTotalPrice / (service.mulchYards || 1),
                discount_percent: 0,
                item_type: 'service',
                service_code: 'gardening_mulch'
              });
            }
          } else if (key === 'lawn') {
            // Calculate lawn seasonal price
            // QR-032: no dates = 0 weeks, matching the pricing endpoint.
            const weeks = calcWeeks(service.startDate, service.endDate);
            items.push({
              description: `Lawn Care - ${service.maintenance}`,
              quantity: weeks,
              unit_price: service.weekPrice,
              discount_percent: 0,
              item_type: 'service',
              service_code: 'lawn_care'
            });
            if (service.bagging) {
              items.push({
                description: 'Lawn Bagging Service',
                quantity: weeks,
                unit_price: service.weekPriceBagging,
                discount_percent: 0,
                item_type: 'service',
                service_code: 'lawn_bagging'
              });
            }
          } else {
            // Generic service item
            items.push({
              description: section?.title || key,
              quantity: 1,
              unit_price: service.seasonalPrice || service.sum || service.baseSum || service.disposalFee || 100,
              discount_percent: 0,
              item_type: 'service',
              service_code: key
            });
          }
          
          // Store detailed service configuration
          serviceConfigs.push({
            service_type: key,
            config_data: service
          });
        }
      });

      const calculatedTotal = savePricing.grandTotal || 0;
      
      const payload = { 
        ...formData, 
        record_price_adjustment: pendingAdjustmentRef.current,
        quote_type_id: config.quoteTypeId, // Dynamic based on quote type
        service_type: isCommercial ? 'Commercial Services' : 
                     isConstruction ? 'Construction Services' : 'Residential Services',
        items,
        service_configs: serviceConfigs,
        property_info: propertyInfo,
        calculated_total: calculatedTotal, // Send the frontend's calculated total
        ...(isCommercial ? { billing_info: billingInfo } : {})
      };
      if (!isCommercial || formData.extra_conditions === loadedExtraConditionsRef.current) {
        delete payload.extra_conditions;
      }
      
      const effectiveQuoteId = quote?.id || createdQuoteId;
      const method = effectiveQuoteId ? 'PUT' : 'POST';
      const endpoint = effectiveQuoteId ? `api/quotes/${effectiveQuoteId}` : 'api/quotes';
      const res = await makeApiCall(endpoint, {
        method,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      if (isCommercial) loadedExtraConditionsRef.current = formData.extra_conditions || '';

      const savedQuoteId = effectiveQuoteId || data.quote?.id;
      if (savedQuoteId && !effectiveQuoteId) {
        setCreatedQuoteId(savedQuoteId);
      }

      // TKT-298: a diagram selected on a brand-new quote is uploaded
      // immediately after the quote record is created, within the same Save
      // action. Staff no longer need to leave and reopen the quote.
      if (pendingSiteDiagram && savedQuoteId) {
        const diagramForm = new FormData();
        diagramForm.append('site_diagram', pendingSiteDiagram);
        diagramForm.append('quote_id', savedQuoteId);
        const uploadResponse = await makeApiCall('/api/files/upload/site-diagram', {
          method: 'POST',
          body: diagramForm
        });
        const uploadData = await uploadResponse.json().catch(() => ({}));
        if (!uploadResponse.ok) {
          throw new Error(uploadData.error || t('quotes.siteDiagramUploadFailed'));
        }
        setPendingSiteDiagram(null);
        setPendingSiteDiagramPreview('');
      }

      pendingAdjustmentRef.current = null;
      onSuccess && onSuccess(data.quote || true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerDisplayName = (customer) => (
    customer?.company_name ||
    `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() ||
    t('quotes.unnamedCustomer', 'Unnamed Customer')
  );

  const getCustomerAddress = (customer) => {
    if (!customer) return '';
    return [
      customer.street_address,
      [customer.city, customer.province, customer.postal_code].filter(Boolean).join(', ')
    ].filter(Boolean).join(' • ');
  };

  const renderCustomerSection = () => (
    <Grid container spacing={3}>
      {/* Customer Search - Full Width */}
      <Grid size={12}>
        <Autocomplete
          fullWidth
          options={customerOptions}
          value={selectedCustomer}
          onChange={(event, newValue) => {
            setSelectedCustomer(newValue);
            handleChange('customer_id', newValue?.id || '');
            
            // Auto-populate property type based on customer type
            if (newValue?.customer_type && lookups.propertyTypes) {
              const matchingPropertyType = lookups.propertyTypes.find(pt => 
                pt.type_name?.toLowerCase() === newValue.customer_type?.toLowerCase()
              );
              if (matchingPropertyType) {
                handleChange('property_type_id', matchingPropertyType.id);
              }
            }
          }}
          inputValue={customerSearchTerm}
          onInputChange={(event, newInputValue) => {
            setCustomerSearchTerm(newInputValue);
          }}
          filterOptions={(options) => options}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            const name = getCustomerDisplayName(option);
            const address = getCustomerAddress(option);
            return address ? `${name} — ${address}` : name;
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body1" fontWeight="bold">
                  {getCustomerDisplayName(option)}
                </Typography>
                {getCustomerAddress(option) && (
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                    {getCustomerAddress(option)}
                  </Typography>
                )}
                {option.email_address && (
                  <Typography variant="body2" color="textSecondary">
                    {option.email_address}
                  </Typography>
                )}
                {option.phone_number && (
                  <Typography variant="body2" color="textSecondary">
                    {option.phone_number}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          loading={customerLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={isCommercial ? t('quotes.searchCommercialCustomer', 'Search Commercial Customer *') : t('quotes.searchCustomer', 'Search Customer *')}
              placeholder={isCommercial ? t('quotes.searchCommercialCustomerPlaceholder', 'Type to search commercial customers...') : t('quotes.searchCustomerPlaceholder', 'Type to search customers...')}
              helperText={selectedCustomer && getCustomerAddress(selectedCustomer)
                ? t('quotes.selectedCustomerAddressHelper', 'Selected address: {{address}}', { address: getCustomerAddress(selectedCustomer) })
                : t('quotes.typeAtLeastTwoCharacters', 'Type at least 2 characters to search')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {customerLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={
            customerSearchTerm.length < 2 
              ? t('quotes.typeAtLeastTwoCharacters', 'Type at least 2 characters to search')
              : customerLoading 
                ? t('common.searching', 'Searching...')
                : t('quotes.noCustomersFound', 'No customers found')
          }
        />
      </Grid>

      {/* Customer Details Display - Always Visible */}
      <Grid size={12}>
        <Paper sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: config.color, fontWeight: 'bold' }}>
          {isCommercial ? 'Selected Commercial Customer Details' : 'Selected Customer Details'}
        </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Name
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ minHeight: '24px' }}>
                  {selectedCustomer ? (
                    getCustomerDisplayName(selectedCustomer)
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No customer selected</span>
                  )}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ minHeight: '24px' }}>
                  {selectedCustomer?.email_address || (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                  )}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ minHeight: '24px' }}>
                  {selectedCustomer?.phone_number || (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Address
                </Typography>
                <Typography variant="body1" sx={{ minHeight: '24px' }}>
                  {selectedCustomer && getCustomerAddress(selectedCustomer) ? (
                    <>
                      {getCustomerAddress(selectedCustomer).split(' • ').map((line, index) => (
                        <React.Fragment key={line}>
                          {line}
                          {index === 0 && <br />}
                        </React.Fragment>
                      ))}
                    </>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                  )}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Customer Type
                </Typography>
                <Box sx={{ minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                  {selectedCustomer?.customer_type ? (
                    <Chip 
                      label={selectedCustomer.customer_type} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                  )}
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Customer Since
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ minHeight: '20px' }}>
                  {selectedCustomer?.created_at ? 
                    new Date(selectedCustomer.created_at).toLocaleDateString() : 
                    <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                  }
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Row 2: Property Type - Full Width for Proper Dropdown Display */}
      <Grid size={12}>
        <FormControl fullWidth sx={{ minWidth: 250 }}>
          <InputLabel>Property Type</InputLabel>
          <Select 
            value={formData.property_type_id} 
            onChange={e => handleChange('property_type_id', e.target.value)}
            label="Property Type"
          >
            {lookups.propertyTypes?.map(pt => (
              <MenuItem key={pt.id} value={pt.id}>{pt.type_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Sales rep whose signature goes on the contract */}
      <Grid size={12}>
        <FormControl fullWidth sx={{ minWidth: 250 }}>
          <InputLabel id="quote-sales-rep-label">{t('quoteForm.salesRep.label')}</InputLabel>
          <Select
            labelId="quote-sales-rep-label"
            value={formData.sales_person_id || ''}
            onChange={e => handleChange('sales_person_id', e.target.value)}
            label={t('quoteForm.salesRep.label')}
            inputProps={{ 'aria-label': t('quoteForm.salesRep.label') }}
          >
            <MenuItem value="">
              <em>
                {(() => {
                  const signer = lookups.salesPersons?.find(sp => sp.is_default_signer);
                  return signer
                    ? t('quoteForm.salesRep.defaultSigner', { name: signer.display_name })
                    : t('quoteForm.salesRep.defaultSignerNone');
                })()}
              </em>
            </MenuItem>
            {lookups.salesPersons?.map(sp => (
              <MenuItem key={sp.id} value={sp.id}>
                {[sp.display_name, sp.position_name].filter(Boolean).join(' / ')}
                {!sp.has_signature && ` (${t('quoteForm.salesRep.noSignature')})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Row 3: Quote Reference - Full Width */}
      <Grid size={12}>
        <TextField 
          fullWidth 
          label="Quote Reference" 
          value={formData.quote_reference} 
          onChange={e => handleChange('quote_reference', e.target.value)} 
          placeholder="Optional reference number or code"
        />
      </Grid>



      {/* Contract term and optional renewal dates */}
      <Grid size={12}>
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#000062' }}>
              {t('quoteForm.contractTerm.title', 'Contract Term')}
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label={t('quoteForm.contractTerm.startDate', 'Contract Start Date')}
                value={formData.contract_date_1 || ''}
                onChange={e => handleChange('contract_date_1', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': t('quoteForm.contractTerm.startDate', 'Contract Start Date') }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label={t('quoteForm.contractTerm.endDate', 'Contract End Date')}
                value={formData.contract_date_2 || ''}
                onChange={e => handleChange('contract_date_2', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': t('quoteForm.contractTerm.endDate', 'Contract End Date') }}
              />
            </Grid>
          </Grid>
          {!isConstruction && (
            <Box sx={{ px: 2, pb: 2 }} role="group" aria-labelledby="billing-seasons-heading">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box>
                  <Typography id="billing-seasons-heading" variant="subtitle1" sx={{ fontWeight: 700, color: '#000062' }}>
                    {t('quoteForm.billingSeasons.title', 'Billing Months')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('quoteForm.billingSeasons.helper', 'One bill per month after each season start. Move a start date forward to skip months or shorten the season.')}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={resetBillingSeasons}
                  aria-label={t('quoteForm.billingSeasons.resetAria', 'Reset billing months to the standard seasons')}
                >
                  {t('quoteForm.billingSeasons.reset', 'Reset to standard seasons')}
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <BillingSeasonRow
                    idPrefix="summer-season"
                    title={t('quoteForm.billingSeasons.summer', 'Summer Months')}
                    start={propertyInfo.summerStartDate}
                    end={propertyInfo.summerEndDate}
                    onChange={(edge, value) => handleSeasonDateChange('summer', edge, value)}
                    locale={i18n.language}
                    t={t}
                  />
                </Grid>
                <Grid size={12}>
                  <BillingSeasonRow
                    idPrefix="winter-season"
                    title={t('quoteForm.billingSeasons.winter', 'Winter Months')}
                    start={propertyInfo.winterStartDate}
                    end={propertyInfo.winterEndDate}
                    onChange={(edge, value) => handleSeasonDateChange('winter', edge, value)}
                    locale={i18n.language}
                    t={t}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          <Box sx={{ px: 2, pb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_renew || false}
                onChange={e => handleChange('auto_renew', e.target.checked)}
                inputProps={{ 'aria-label': t('quoteForm.autoRenew.label', 'Auto-Renew Contract') }}
              />
            }
            label={t('quoteForm.autoRenew.label', 'Auto-Renew Contract')}
          />
          {formData.auto_renew && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('quoteForm.autoRenew.date3', 'First Renewal Date')}
                  value={formData.contract_date_3 || ''}
                  onChange={e => handleChange('contract_date_3', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'aria-label': t('quoteForm.autoRenew.date3', 'First Renewal Date') }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('quoteForm.autoRenew.date4', 'Second Renewal Date')}
                  value={formData.contract_date_4 || ''}
                  onChange={e => handleChange('contract_date_4', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'aria-label': t('quoteForm.autoRenew.date4', 'Second Renewal Date') }}
                />
              </Grid>
            </Grid>
          )}
          </Box>
        </Box>
      </Grid>

      {/* Commercial contract "Other - Extra Conditions" page */}
      {isCommercial && (
        <Grid size={12}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#000062' }}>
                {t('quoteForm.extraConditions.title', 'Extra Conditions')}
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={12}
                label={t('quoteForm.extraConditions.label', 'Other - Extra Conditions')}
                value={formData.extra_conditions || ''}
                onChange={e => handleChange('extra_conditions', e.target.value)}
                placeholder={t('quoteForm.extraConditions.placeholder', 'Site-specific conditions added to the contract for the customer to initial')}
                helperText={t('quoteForm.extraConditions.helper', 'Printed on its own page of the contract. Leave blank if there are none. Separate paragraphs with a blank line.')}
                inputProps={{ 'aria-label': t('quoteForm.extraConditions.label', 'Other - Extra Conditions') }}
              />
            </Box>
          </Box>
        </Grid>
      )}

      {/* Row 5: Description - Full Width, Larger */}
      <Grid size={12}>
        <TextField 
          fullWidth 
          label="Description" 
          multiline 
          minRows={4} 
          maxRows={8}
          value={formData.description} 
          onChange={e => handleChange('description', e.target.value)} 
          placeholder="Enter detailed description of the services and any special requirements..."
          helperText="Provide details about the services, property specifics, or any special requirements"
        />
      </Grid>
    </Grid>
  );

  // Property Section
  // TKT-231 (Doug §7.A): gardenSqFt feeds gardening/mulch/edge calculations.
  const propertySizeFields = [
    { key: 'squareFt', label: t('quoteForm.propertyFields.squareFt', 'Property Size (sq ft)'), helper: t('quoteForm.propertyFields.squareFtHelper', 'Total property square footage') },
    { key: 'drivewaySqFt', label: t('quoteForm.propertyFields.drivewaySqFt', 'Driveway Size (sq ft)'), helper: t('quoteForm.propertyFields.drivewaySqFtHelper', 'Driveway square footage') },
    { key: 'parkingLotSize', label: t('quoteForm.propertyFields.parkingLotSize', 'Parking Lot Size (sq ft)'), helper: t('quoteForm.propertyFields.parkingLotSizeHelper', 'Parking lot square footage') },
    { key: 'citySidewalks', label: t('quoteForm.propertyFields.citySidewalks', 'City Sidewalks (sq ft)'), helper: t('quoteForm.propertyFields.citySidewalksHelper', 'City sidewalk square footage') },
    { key: 'internalSidewalks', label: t('quoteForm.propertyFields.internalSidewalks', 'Internal Sidewalks (sq ft)'), helper: t('quoteForm.propertyFields.internalSidewalksHelper', 'Internal sidewalk square footage') },
    { key: 'gardenSqFt', label: t('quoteForm.propertyFields.gardenSqFt', 'Garden Square Footage (sq ft)'), helper: t('quoteForm.propertyFields.gardenSqFtHelper', 'Garden / planting bed square footage') }
  ];

  const renderPropertySection = () => (
    <Grid container spacing={3}>
      {propertySizeFields.map(field => (
        <Grid key={field.key} size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            fullWidth
            type="number"
            label={field.label}
            value={propertyInfo[field.key]}
            onChange={e => setPropertyInfo(prev => ({ ...prev, [field.key]: numVal(e.target.value) }))}
            helperText={field.helper}
            inputProps={{ min: 0, 'aria-label': field.label }}
            InputProps={{
              endAdornment: (
                <SqFtCalculatorAdornment
                  value={propertyInfo[field.key]}
                  fieldLabel={field.label}
                  onApply={value => setPropertyInfo(prev => ({ ...prev, [field.key]: value }))}
                />
              )
            }}
          />
        </Grid>
      ))}
    </Grid>
  );

  // Spring Section
  const renderSpringSection = () => {
    const springPrice = pricing.spring.total || 0;
    
    return (
      <Box>
        <FormControlLabel
          control={
            <Switch 
              checked={services.spring.enabled}
              onChange={e => handleServiceChange('spring', 'enabled', e.target.checked)}
            />
          }
          label="Include Spring Clean-Up"
          sx={{ mb: 3 }}
        />
        
        {services.spring.enabled && (
          <>
            {/* Spring Clean-Up Configuration */}
            <Box sx={{ mb: 4, p: 3, bgcolor: '#fff8e1', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#ffeb3b', fontWeight: 'bold' }}>
                Spring Clean-Up
              </Typography>
              
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={services.spring.workSelected}
                        onChange={e => handleServiceChange('spring', 'workSelected', e.target.checked)}
                      />
                    }
                    label="Debris clean-up and lawn / garden prep. and shrub trim."
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Seasonal Price</Typography>
                    <Typography variant="h6" color="primary.main">{formatCurrencyWithSymbol(springPrice)}</Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.billedTimeMinutes')}
                    value={services.spring.timeMinutes}
                    onChange={e => handleServiceChange('spring', 'timeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.billedTimeHelper')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Disposal Fee ($)"
                    value={services.spring.disposalFee}
                    onChange={e => handleServiceChange('spring', 'disposalFee', numVal(e.target.value, true))}
                  />
                </Grid>
              </Grid>

              {/* TKT-179: manual seasonal-price override. When non-zero,
                  the backend pricing endpoint uses this value instead of
                  rate * time + disposal. Lets the office preserve the
                  exact legacy seasonal total. */}
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Manual Seasonal Price Override ($)"
                    value={services.spring.sum || ''}
                    onChange={(e) => handleServiceChange('spring', 'sum', numVal(e.target.value, true))}
                    inputProps={{ step: 0.01, min: 0, 'aria-label': 'Spring seasonal price manual override' }}
                    helperText="Leave blank to use the calculated total. Enter a value to lock the seasonal price to it."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Work Time Section */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#ffeb3b', fontWeight: 'bold' }}>{t('quoteForm.timeLabels.crewWorkTime')}</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.springCrewWorkTimeMinutes')}
                    value={services.spring.workTimeMinutes}
                    onChange={e => handleServiceChange('spring', 'workTimeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.crewWorkTimeHelper')}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Sweeping Section
  const renderSweepingSection = () => {
    const sweepingPrice = pricing.sweeping?.total || 0;
    
    return (
      <Box>
        <FormControlLabel
          control={
            <Switch 
              checked={services.sweeping.enabled}
              onChange={e => handleServiceChange('sweeping', 'enabled', e.target.checked)}
              aria-label="Enable parking lot sweeping services"
            />
          }
          label="Include Parking Lot Sweeping"
          sx={{ mb: 3 }}
        />
        
        {services.sweeping.enabled && (
          <>
            <Box sx={{ mb: 4, p: 3, bgcolor: '#eceff1', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#546e7a', fontWeight: 'bold' }}>
                Sweeping Configuration
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Time (minutes)"
                    type="number"
                    size="small"
                    value={services.sweeping.timeMinutes}
                    onChange={e => handleServiceChange('sweeping', 'timeMinutes', numVal(e.target.value))}
                    inputProps={{ min: 15, max: 480, 'aria-label': 'Sweeping time in minutes' }}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Disposal Fee"
                    type="number"
                    size="small"
                    value={services.sweeping.disposalFee}
                    onChange={e => handleServiceChange('sweeping', 'disposalFee', numVal(e.target.value, true))}
                    InputProps={{ startAdornment: '$' }}
                    inputProps={{ min: 0, step: 0.01, 'aria-label': 'Disposal fee amount' }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
            
            {/* Total */}
            <Box sx={{ mt: 3, p: 2, bgcolor: sweepingPrice > 0 ? '#546e7a' : '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: sweepingPrice > 0 ? 'white' : '#999', fontWeight: 'bold' }}>
                Sweeping Total
              </Typography>
              <Typography variant="h5" sx={{ color: sweepingPrice > 0 ? 'white' : '#999', fontWeight: 'bold' }}>
                {formatCurrencyWithSymbol(sweepingPrice)}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Lawn Section
  const renderLawnSection = () => {
    const weeks = calcWeeks(services.lawn.startDate, services.lawn.endDate);
    const propertyInfo = selectedCustomer ? {
      squareFt: selectedCustomer.property_size || 1000,
      parkingLot: selectedCustomer.parking_lot_size || 0
    } : {};
    
    // Real-time pricing calculations
    const mowingWeekly = services.lawn.weekPrice || 0;
    const baggingWeekly = services.lawn.bagging ? (services.lawn.weekPriceBagging || 0) : 0;
    const mowingSeasonal = weeks * mowingWeekly;
    const baggingSeasonal = weeks * baggingWeekly;

    const getGeneralQuoteRate = (alias, fallback) => {
      const rate = serviceRates[alias];
      return rate?.basic ?? rate?.fixed ?? fallback;
    };
    // Trash bag pricing uses Admin Settings > General Quote Pricing.
    // Legacy formula (Quote.cs LawnTrashBagTotalPrice, identical to the snow
    // one): the seasonal total is perBagRate x Time(week) x 0.75, plus the
    // extra-bag rate for each bag beyond the first. It is a seasonal figure,
    // not a weekly rate multiplied by the number of weeks -- pricing it that
    // way ignored Time(week) entirely and overstated the line.
    const isOnSiteBags = services.lawn.trashBagType === 'OnSite';
    const trashBagPricePerBag = isOnSiteBags
      ? getGeneralQuoteRate('GeneralQuotePricePerBagDisposedOnSite', 35)
      : getGeneralQuoteRate('GeneralQuotePricePerBagDisposedOffSite', 55);
    const trashBagExtraPrice = isOnSiteBags
      ? getGeneralQuoteRate('GeneralQuotePriceExtraBagDisposedOnSite', 8)
      : getGeneralQuoteRate('GeneralQuotePriceExtraBagDisposedOffSite', 20);
    const calculateLawnTrashBagSeasonal = () => {
      const qty = Number(services.lawn.trashBagQuantity) || 0;
      const perWeek = Number(services.lawn.trashBagTimePerWeek) || 0;
      if (!services.lawn.trashBagServiceEnabled || qty <= 0 || perWeek <= 0) return 0;
      let total = trashBagPricePerBag * perWeek * 0.75;
      if (qty > 1) total += trashBagExtraPrice * (qty - 1);
      else total *= qty;
      return Math.round(total * 100) / 100;
    };
    const trashBagSeasonal = calculateLawnTrashBagSeasonal();
    // Legacy prints the Lawn Maintenance header total as mowing + bagging +
    // trash bags; the form previously showed only the mowing seasonal, so
    // trash bags looked like they were being left out of the quote.
    const lawnSeasonalTotal = mowingSeasonal + baggingSeasonal + trashBagSeasonal;

    return (
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={services.lawn.enabled}
              onChange={e => handleServiceChange('lawn', 'enabled', e.target.checked)}
            />
          }
          label={isCommercial ? "Include Commercial Lawn Maintenance" : "Include Lawn Maintenance"}
          sx={{ mb: 3 }}
        />

        {services.lawn.enabled && (
          <>
            {/* Configuration */}
            <Box sx={{ mb: 4, p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#4caf50', fontWeight: 'bold' }}>
                {isCommercial ? 'Commercial Lawn Maintenance' : 'Lawn Maintenance'}
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Service Level</InputLabel>
                    <Select
                      value={services.lawn.maintenance}
                      onChange={e => handleServiceChange('lawn', 'maintenance', e.target.value)}
                      label="Service Level"
                    >
                      <MenuItem value="Basic">{isCommercial ? 'Basic Commercial' : 'Basic'}</MenuItem>
                      <MenuItem value="Premium">{isCommercial ? 'Premium Commercial' : 'Premium'}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={services.lawn.startDate}
                    onChange={e => handleServiceChange('lawn', 'startDate', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    value={services.lawn.endDate}
                    onChange={e => handleServiceChange('lawn', 'endDate', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.billedTimeMins')}
                    value={services.lawn.timeMinutes}
                    onChange={e => handleServiceChange('lawn', 'timeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.billedTimeHelper')}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Chip label={`Weeks: ${weeks}`} size="small" />
              </Box>
            </Box>

            {/* Main service row: Mowing */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={services.lawn.mowing}
                        onChange={e => handleServiceChange('lawn', 'mowing', e.target.checked)}
                      />
                    }
                    label={isCommercial ? "Commercial Mowing & Maintenance - Mowing, line trimming, edging, and debris cleanup" : "Mowing, line trimming, garbage and debris clean up"}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weekly Price ($)"
                    value={services.lawn.weekPrice}
                    onChange={(e) => {
                      const v = numVal(e.target.value, true);
                      setServices((prev) => ({
                        ...prev,
                        lawn: {
                          ...prev.lawn,
                          weekPrice: v,
                          weekBasePrice: v,
                          weekPriceManualOverride: true,
                        },
                      }));
                    }}
                    inputProps={{ step: 0.01, min: 0, 'aria-label': 'Mowing weekly price' }}
                    helperText={
                      services.lawn.weekPriceManualOverride
                        ? t('quoteForm.lawn.weekPriceOverride')
                        : t('quoteForm.lawn.weekPriceAuto')
                    }
                  />
                  {services.lawn.weekPriceManualOverride && (
                    <Button
                      size="small"
                      onClick={() => {
                        // Clear the baseline so the auto price is recalculated now.
                        lawnPriceBaselineRef.current = null;
                        setServices((prev) => ({
                          ...prev,
                          lawn: { ...prev.lawn, weekPriceManualOverride: false },
                        }));
                      }}
                      sx={{ mt: 0.5 }}
                      aria-label="Reset mowing weekly price to auto-calculated value"
                    >
                      Reset to auto
                    </Button>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Seasonal Price</Typography>
                    <Typography variant="h6" color="primary.main">{formatCurrencyWithSymbol(mowingSeasonal || 0)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Common lawn areas only */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={services.lawn.commonLawnAreaOnly}
                    onChange={e => handleServiceChange('lawn', 'commonLawnAreaOnly', e.target.checked)}
                  />
                }
                label="Common lawn areas only"
              />
            </Box>

            {/* Bagging */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={services.lawn.bagging}
                        onChange={e => handleServiceChange('lawn', 'bagging', e.target.checked)}
                      />
                    }
                    label="Bagging"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weekly Price (Bagging $)"
                    value={services.lawn.weekPriceBagging}
                    onChange={(e) => {
                      const v = numVal(e.target.value, true);
                      setServices((prev) => ({
                        ...prev,
                        lawn: {
                          ...prev.lawn,
                          weekPriceBagging: v,
                          weekPriceBaseBagging: v,
                          weekPriceBaggingManualOverride: true,
                        },
                      }));
                    }}
                    inputProps={{ step: 0.01, min: 0, 'aria-label': 'Bagging weekly price' }}
                    helperText={t('quoteForm.lawn.baggingPriceHelp')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Seasonal Price</Typography>
                    <Typography variant="h6" color="primary.main">{formatCurrencyWithSymbol(baggingSeasonal || 0)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Truck Price */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#4caf50', fontWeight: 'bold' }}>
                {t('quotes.lawn.truckPriceTitle', 'Truck Cost')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quotes.lawn.truckPrice', 'Truck Price ($)')}
                    value={services.lawn.truckPrice || 0}
                    onChange={e => handleServiceChange('lawn', 'truckPrice', numVal(e.target.value))}
                    inputProps={{ 'aria-label': t('quotes.lawn.truckPrice', 'Truck Price ($)') }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Trash Bags */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#4caf50', fontWeight: 'bold' }}>Trash Bags</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={services.lawn.trashBagServiceEnabled}
                        onChange={e => handleServiceChange('lawn', 'trashBagServiceEnabled', e.target.checked)}
                      />
                    }
                    label="Trash bags service"
                  />
                </Grid>
                {services.lawn.trashBagServiceEnabled && (
                  <>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormControl fullWidth sx={{ minWidth: 160 }}>
                        <InputLabel>Service Type</InputLabel>
                        <Select
                          value={services.lawn.trashBagType}
                          onChange={e => handleServiceChange('lawn', 'trashBagType', e.target.value)}
                          label="Service Type"
                        >
                          <MenuItem value="OnSite">On Site</MenuItem>
                          <MenuItem value="OffSite">Off Site</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="# of Bags (week)"
                        value={services.lawn.trashBagQuantity}
                        onChange={e => handleServiceChange('lawn', 'trashBagQuantity', numVal(e.target.value))}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Time (week)"
                        value={services.lawn.trashBagTimePerWeek}
                        onChange={e => handleServiceChange('lawn', 'trashBagTimePerWeek', numVal(e.target.value))}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {t('quoteForm.lawn.trashBagRate', 'Rate / bag')}
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrencyWithSymbol(trashBagPricePerBag)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('quoteForm.lawn.trashBagExtraRate', '{{amount}} per extra bag', {
                            amount: formatCurrencyWithSymbol(trashBagExtraPrice)
                          })}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {t('quoteForm.lawn.trashBagSeasonal', 'Seasonal')}
                        </Typography>
                        <Typography variant="h6" color="primary.main" aria-live="polite">
                          {formatCurrencyWithSymbol(trashBagSeasonal || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('quoteForm.lawn.trashBagIncluded', 'Included in the Lawn Maintenance total')}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            {/* Lawn Maintenance total. Legacy prints mowing + bagging + trash
                bags as one Lawn Maintenance seasonal figure. */}
            <Box sx={{ mb: 3, p: 2, bgcolor: lawnSeasonalTotal > 0 ? '#2e7d32' : '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: lawnSeasonalTotal > 0 ? 'white' : '#999', fontWeight: 'bold' }}>
                {t('quoteForm.lawn.seasonalTotal', 'Lawn Maintenance Total')}
              </Typography>
              <Typography variant="h5" sx={{ color: lawnSeasonalTotal > 0 ? 'white' : '#999', fontWeight: 'bold' }} aria-live="polite">
                {formatCurrencyWithSymbol(lawnSeasonalTotal)}
              </Typography>
            </Box>

            {/* Work Time */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#4caf50', fontWeight: 'bold' }}>{t('quoteForm.timeLabels.crewWorkTime')}</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.lawnCrewWorkTimeMins')}
                    value={services.lawn.workTimeMinutes}
                    onChange={e => handleServiceChange('lawn', 'workTimeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.crewWorkTimeHelper')}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Weekly price is the controlling value. Editing the seasonal price
  // back-calculates weekly so the two always describe the same agreement.
  // QR-040: blank garden dates = 0 weeks = $0, as in legacy.
  const gardenPricingWeeks = calcWeeks(services.gardening.startDate, services.gardening.endDate);
  const gardenLineWeekly = (frequency, minutes, overrideFlag, overrideWeekly) => {
    if (overrideFlag) {
      const agreed = Number(overrideWeekly);
      return Number.isNaN(agreed) ? 0 : agreed;
    }
    return calculateGardeningLineWeekly(frequency, minutes);
  };
  const renderGardenPriceFields = ({ frequency, minutes, weeklyField, flagField, label }) => {
    if (!frequency) return null;
    const override = !!services.gardening[flagField];
    const weekly = gardenLineWeekly(frequency, minutes, override, services.gardening[weeklyField]);
    const seasonal = Math.round(weekly * gardenPricingWeeks * 100) / 100;
    return (
      <>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            type="number"
            label={t('quoteForm.gardening.weeklyPrice', 'Weekly Price ($)')}
            value={override ? services.gardening[weeklyField] : Number(weekly.toFixed(2))}
            onChange={(e) => {
              const v = numVal(e.target.value, true);
              setServices((prev) => ({
                ...prev,
                gardening: { ...prev.gardening, [weeklyField]: v, [flagField]: true },
              }));
            }}
            inputProps={{
              step: 0.01,
              min: 0,
              'aria-label': t('quoteForm.gardening.weeklyPriceAria', '{{label}} weekly price', { label }),
            }}
            helperText={override
              ? t('quoteForm.gardening.overrideActive', 'Manual override active. Weekly price controls the seasonal price.')
              : t('quoteForm.gardening.weeklyHelper', 'Calculated from billed time. Type a value to override.')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            type="number"
            label={t('quoteForm.gardening.seasonalPrice', 'Seasonal Price ($)')}
            value={seasonal}
            onChange={(e) => {
              const seasonalValue = Number(numVal(e.target.value, true)) || 0;
              const nextWeekly = Math.round((seasonalValue / gardenPricingWeeks) * 100) / 100;
              setServices((prev) => ({
                ...prev,
                gardening: { ...prev.gardening, [weeklyField]: nextWeekly, [flagField]: true },
              }));
            }}
            inputProps={{
              step: 0.01,
              min: 0,
              'aria-label': t('quoteForm.gardening.seasonalPriceAria', '{{label}} seasonal price', { label }),
            }}
            helperText={t('quoteForm.gardening.seasonalHelper', '{{weeks}} weeks. Editing this sets the weekly price.', { weeks: gardenPricingWeeks })}
          />
          {override && (
            <Button
              size="small"
              onClick={() => setServices((prev) => ({
                ...prev,
                gardening: { ...prev.gardening, [flagField]: false, [weeklyField]: 0 },
              }))}
              sx={{ mt: 0.5 }}
              aria-label={t('quoteForm.gardening.resetPrice', 'Reset {{label}} price to calculated', { label })}
            >
              {t('quoteForm.gardening.resetToCalculated', 'Reset to calculated')}
            </Button>
          )}
        </Grid>
      </>
    );
  };

  // Gardening Section
  const renderGardeningSection = () => (
    <Box>
      <FormControlLabel
        control={
          <Switch 
            checked={services.gardening.enabled}
            onChange={e => handleServiceChange('gardening', 'enabled', e.target.checked)}
          />
        }
        label="Include Gardening Services"
        sx={{ mb: 3 }}
      />
      
      {services.gardening.enabled && (
        <>
          {/* Configuration Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#e91e63', fontWeight: 'bold' }}>
              Service Configuration
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Service Level</InputLabel>
                  <Select
                    value={services.gardening.tariff}
                    onChange={e => handleServiceChange('gardening', 'tariff', e.target.value)}
                    label="Service Level"
                  >
                    <MenuItem value="Basic">Basic</MenuItem>
                    <MenuItem value="Premium">Premium</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={services.gardening.startDate}
                  onChange={e => handleServiceChange('gardening', 'startDate', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={services.gardening.endDate}
                  onChange={e => handleServiceChange('gardening', 'endDate', e.target.value)}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Chip
                label={t('quoteForm.gardening.weeks', 'Weeks: {{count}}', {
                  count: calcWeeks(services.gardening.startDate, services.gardening.endDate)
                })}
                size="small"
                aria-label={t('quoteForm.gardening.weeksAria', 'Garden maintenance weeks')}
              />
            </Box>
          </Box>

          {/* Main Services Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#fff8e1', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#e91e63', fontWeight: 'bold' }}>
              Main Services
            </Typography>
            
            {/* De-weeding Service */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>De-weeding Frequency</InputLabel>
                    <Select
                      value={services.gardening.deWeeding}
                      onChange={e => handleServiceChange('gardening', 'deWeeding', parseInt(e.target.value))}
                      label="De-weeding Frequency"
                    >
                      <MenuItem value={0}>None</MenuItem>
                      <MenuItem value={1}>Weekly</MenuItem>
                      <MenuItem value={2}>Bi-weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {services.gardening.deWeeding > 0 && (
                  <>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('quoteForm.timeLabels.billedTimeMinutes')}
                        value={services.gardening.deWeedingTime}
                        onChange={e => handleServiceChange('gardening', 'deWeedingTime', numVal(e.target.value))}
                        helperText={t('quoteForm.timeLabels.billedTimeHelper')}
                      />
                    </Grid>
                    {renderGardenPriceFields({
                      frequency: services.gardening.deWeeding,
                      minutes: services.gardening.deWeedingTime,
                      weeklyField: 'deWeedingWeeklyPrice',
                      flagField: 'deWeedingPriceManualOverride',
                      label: t('quoteForm.gardening.deWeeding', 'De-weeding'),
                    })}
                  </>
                )}
              </Grid>
            </Box>

            {/* Soil Tilling Service */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Soil Tilling Frequency</InputLabel>
                    <Select
                      value={services.gardening.soilTilling}
                      onChange={e => handleServiceChange('gardening', 'soilTilling', parseInt(e.target.value))}
                      label="Soil Tilling Frequency"
                    >
                      <MenuItem value={0}>None</MenuItem>
                      <MenuItem value={1}>Weekly</MenuItem>
                      <MenuItem value={2}>Bi-weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {services.gardening.soilTilling > 0 && (
                  <>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('quoteForm.timeLabels.billedTimeMinutes')}
                        value={services.gardening.soilTillingTime}
                        onChange={e => handleServiceChange('gardening', 'soilTillingTime', numVal(e.target.value))}
                        helperText={t('quoteForm.timeLabels.billedTimeHelper')}
                      />
                    </Grid>
                    {renderGardenPriceFields({
                      frequency: services.gardening.soilTilling,
                      minutes: services.gardening.soilTillingTime,
                      weeklyField: 'soilTillingWeeklyPrice',
                      flagField: 'soilTillingPriceManualOverride',
                      label: t('quoteForm.gardening.soilTilling', 'Soil tilling'),
                    })}
                  </>
                )}
              </Grid>
            </Box>

            {/* Edge Maintenance Service */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Edge Maintenance Frequency</InputLabel>
                    <Select
                      value={services.gardening.edge}
                      onChange={e => handleServiceChange('gardening', 'edge', parseInt(e.target.value))}
                      label="Edge Maintenance Frequency"
                    >
                      <MenuItem value={0}>None</MenuItem>
                      <MenuItem value={1}>Weekly</MenuItem>
                      <MenuItem value={2}>Bi-weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {services.gardening.edge > 0 && (
                  <>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('quoteForm.timeLabels.billedTimeMinutes')}
                        value={services.gardening.edgeTime}
                        onChange={e => handleServiceChange('gardening', 'edgeTime', numVal(e.target.value))}
                        helperText={t('quoteForm.timeLabels.billedTimeHelper')}
                      />
                    </Grid>
                    {renderGardenPriceFields({
                      frequency: services.gardening.edge,
                      minutes: services.gardening.edgeTime,
                      weeklyField: 'edgeWeeklyPrice',
                      flagField: 'edgePriceManualOverride',
                      label: t('quoteForm.gardening.edge', 'Edge maintenance'),
                    })}
                  </>
                )}
              </Grid>
            </Box>
          </Box>

          {/* Additional Services Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#f3e5f5', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#e91e63', fontWeight: 'bold' }}>
              Additional Gardening Services
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.frontHouseSelected}
                      onChange={e => handleServiceChange('gardening', 'frontHouseSelected', e.target.checked)}
                    />
                  }
                  label="Front of house"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.sideHouseSelected}
                      onChange={e => handleServiceChange('gardening', 'sideHouseSelected', e.target.checked)}
                    />
                  }
                  label="Side of house"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.backHouseSelected}
                      onChange={e => handleServiceChange('gardening', 'backHouseSelected', e.target.checked)}
                    />
                  }
                  label="Back of house"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.frontCondoSelected}
                      onChange={e => handleServiceChange('gardening', 'frontCondoSelected', e.target.checked)}
                    />
                  }
                  label="Front of condo"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.backCondoSelected}
                      onChange={e => handleServiceChange('gardening', 'backCondoSelected', e.target.checked)}
                    />
                  }
                  label="Back of condo"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.commonTreeRingsSelected}
                      onChange={e => handleServiceChange('gardening', 'commonTreeRingsSelected', e.target.checked)}
                    />
                  }
                  label="Common gardens and tree rings"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Weed Spray Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#fff3e0', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#e91e63', fontWeight: 'bold' }}>
              Gardening Weed Spray
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.weedSelected}
                      onChange={e => handleServiceChange('gardening', 'weedSelected', e.target.checked)}
                    />
                  }
                  label="Weed spray (hard surfaces, buildings, curbs, rocks, etc.)"
                />
              </Grid>
              {services.gardening.weedSelected && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Seasonal Price"
                    value={services.gardening.weedSprayTime}
                    onChange={(e) => {
                      // Keep the base in step so the old $6,000 default can't come back.
                      const v = numVal(e.target.value, true);
                      setServices((prev) => ({
                        ...prev,
                        gardening: { ...prev.gardening, weedSprayTime: v, weedBaseSprayTime: v },
                      }));
                    }}
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Mulch Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#e8f5e8', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#e91e63', fontWeight: 'bold' }}>
              Mulch Service
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={services.gardening.mulchSelected}
                      onChange={e => handleServiceChange('gardening', 'mulchSelected', e.target.checked)}
                    />
                  }
                  label="Include Mulch Service"
                />
              </Grid>
              {services.gardening.mulchSelected && (
                <>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.gardening.yardsRequired', 'Yards Required')}
                      value={services.gardening.mulchYards}
                      onChange={e => handleGardeningMulchYardsChange(numVal(e.target.value))}
                      inputProps={{ 'aria-label': t('quoteForm.gardening.yardsRequired', 'Yards Required') }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#ffffff', borderRadius: 1 }} aria-live="polite">
                      <Typography variant="body2" fontWeight="bold">
                        {t('quoteForm.gardening.mulchPrice', 'Mulch Price')}
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrencyWithSymbol(services.gardening.mulchTotalPrice || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* Work Time Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#e91e63', fontWeight: 'bold' }}>
              {t('quoteForm.timeLabels.crewWorkTimeConfiguration')}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quoteForm.timeLabels.deWeedingCrewWorkTimeMinutes')}
                  value={services.gardening.deWeedingWorkTime}
                  onChange={e => handleServiceChange('gardening', 'deWeedingWorkTime', numVal(e.target.value))}
                  helperText={t('quoteForm.timeLabels.crewWorkTimeHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quoteForm.timeLabels.soilTillingCrewWorkTimeMinutes')}
                  value={services.gardening.soilTillingWorkTime}
                  onChange={e => handleServiceChange('gardening', 'soilTillingWorkTime', numVal(e.target.value))}
                  helperText={t('quoteForm.timeLabels.crewWorkTimeHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quoteForm.timeLabels.edgeCrewWorkTimeMinutes')}
                  value={services.gardening.edgeWorkTime}
                  onChange={e => handleServiceChange('gardening', 'edgeWorkTime', numVal(e.target.value))}
                  helperText={t('quoteForm.timeLabels.crewWorkTimeHelper')}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );

  // Annuals/Purchasing Section
  const renderAnnualsSection = () => {
    const annualsPrice = pricing.annuals?.total || 0;
    
    return (
      <Box>
        <FormControlLabel
          control={
            <Switch 
              checked={services.annuals.enabled}
              onChange={e => handleServiceChange('annuals', 'enabled', e.target.checked)}
              aria-label="Enable annuals and purchasing services"
            />
          }
          label="Include Annuals & Purchasing"
          sx={{ mb: 3 }}
        />
        
        {services.annuals.enabled && (
          <>
            <Box sx={{ mb: 4, p: 3, bgcolor: '#fce4ec', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#c2185b', fontWeight: 'bold' }}>
                Annual Flowers & Planting
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={services.annuals.purchasePlantingFlats}
                        onChange={e => handleServiceChange('annuals', 'purchasePlantingFlats', e.target.checked)}
                        aria-label="Enable flats planting"
                      />
                    }
                    label="Purchase & Planting - Flats"
                  />
                  {services.annuals.purchasePlantingFlats && (
                    <TextField
                      label="Flats Base Fee"
                      type="number"
                      size="small"
                      value={services.annuals.purchasePlantingFlatsBaseFee}
                      onChange={e => handleServiceChange('annuals', 'purchasePlantingFlatsBaseFee', numVal(e.target.value, true))}
                      InputProps={{ startAdornment: '$' }}
                      inputProps={{ min: 0, step: 0.01, 'aria-label': 'Flats base fee amount' }}
                      sx={{ mt: 1, width: '100%' }}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={services.annuals.purchasePlantingPots}
                        onChange={e => handleServiceChange('annuals', 'purchasePlantingPots', e.target.checked)}
                        aria-label="Enable pots planting"
                      />
                    }
                    label="Purchase & Planting - Pots"
                  />
                  {services.annuals.purchasePlantingPots && (
                    <TextField
                      label="Pots Base Fee"
                      type="number"
                      size="small"
                      value={services.annuals.purchasePlantingPotsBaseFee}
                      onChange={e => handleServiceChange('annuals', 'purchasePlantingPotsBaseFee', numVal(e.target.value, true))}
                      InputProps={{ startAdornment: '$' }}
                      inputProps={{ min: 0, step: 0.01, 'aria-label': 'Pots base fee amount' }}
                      sx={{ mt: 1, width: '100%' }}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
            
            {/* Total */}
            <Box sx={{ mt: 3, p: 2, bgcolor: annualsPrice > 0 ? '#c2185b' : '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: annualsPrice > 0 ? 'white' : '#999', fontWeight: 'bold' }}>
                Annuals Total
              </Typography>
              <Typography variant="h5" sx={{ color: annualsPrice > 0 ? 'white' : '#999', fontWeight: 'bold' }}>
                {formatCurrencyWithSymbol(annualsPrice)}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Trimming Section
  const renderTrimmingSection = () => {
    const trimmingPrice = pricing.trimming.total || 0;
    
    return (
      <Box>
        <FormControlLabel
          control={
            <Switch 
              checked={services.trimming.enabled}
              onChange={e => handleServiceChange('trimming', 'enabled', e.target.checked)}
            />
          }
          label="Include Seasonal Shrub Pruning"
          sx={{ mb: 3 }}
        />
        
        {services.trimming.enabled && (
          <>
            {/* Trimming Configuration */}
            <Box sx={{ mb: 4, p: 3, bgcolor: '#f3e5f5', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#795548', fontWeight: 'bold' }}>
                Seasonal Shrub Pruning (sessions)
              </Typography>
              
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, md: 8 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={services.trimming.selected}
                        onChange={e => handleServiceChange('trimming', 'selected', e.target.checked)}
                      />
                    }
                    label="Garden shrubs and hedges up to 10 feet in height will be serviced."
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Seasonal Price</Typography>
                    <Typography variant="h6" color="primary.main">{formatCurrencyWithSymbol(trimmingPrice)}</Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Applications"
                    value={services.trimming.applications}
                    onChange={e => handleServiceChange('trimming', 'applications', numVal(e.target.value))}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.billedTimeMinutes')}
                    value={services.trimming.timeMinutes}
                    onChange={e => handleServiceChange('trimming', 'timeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.billedTimeHelper')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Disposal Fee ($)"
                    value={services.trimming.disposalFee}
                    onChange={e => handleServiceChange('trimming', 'disposalFee', numVal(e.target.value, true))}
                  />
                </Grid>
              </Grid>

              {/* TKT-179: manual seasonal-price override. */}
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Manual Seasonal Price Override ($)"
                    value={services.trimming.sum || ''}
                    onChange={(e) => handleServiceChange('trimming', 'sum', numVal(e.target.value, true))}
                    inputProps={{ step: 0.01, min: 0, 'aria-label': 'Trimming seasonal price manual override' }}
                    helperText="Leave blank to use the calculated total. Enter a value to lock the seasonal price to it."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Work Time Section */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#795548', fontWeight: 'bold' }}>{t('quoteForm.timeLabels.crewWorkTime')}</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.trimmingCrewWorkTimeMinutes')}
                    value={services.trimming.workTimeMinutes}
                    onChange={e => handleServiceChange('trimming', 'workTimeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.crewWorkTimeHelper')}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Fall Section
  const renderFallSection = () => {
    const fallPrice = pricing.fall.total || 0;
    
    return (
      <Box>
        <FormControlLabel
          control={
            <Switch 
              checked={services.fall.enabled}
              onChange={e => handleServiceChange('fall', 'enabled', e.target.checked)}
            />
          }
          label="Include Fall Clean-Up"
          sx={{ mb: 3 }}
        />
        
        {services.fall.enabled && (
          <>
            {/* Fall Clean-Up Configuration */}
            <Box sx={{ mb: 4, p: 3, bgcolor: '#fff3e0', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#ff9800', fontWeight: 'bold' }}>
                Fall Clean-Up
              </Typography>
              
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={services.fall.selected}
                        onChange={e => handleServiceChange('fall', 'selected', e.target.checked)}
                      />
                    }
                    label="Weekly leaf clearing, end of season leaf removal, annuals, perennials, shrub trim"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Seasonal Price</Typography>
                    <Typography variant="h6" color="primary.main">{formatCurrencyWithSymbol(fallPrice)}</Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.billedTimeMinutes')}
                    value={services.fall.timeMinutes}
                    onChange={e => handleServiceChange('fall', 'timeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.billedTimeHelper')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Disposal Fee ($)"
                    value={services.fall.disposalFee}
                    onChange={e => handleServiceChange('fall', 'disposalFee', numVal(e.target.value, true))}
                  />
                </Grid>
              </Grid>

              {/* TKT-179: manual seasonal-price override. */}
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Manual Seasonal Price Override ($)"
                    value={services.fall.sum || ''}
                    onChange={(e) => handleServiceChange('fall', 'sum', numVal(e.target.value, true))}
                    inputProps={{ step: 0.01, min: 0, 'aria-label': 'Fall seasonal price manual override' }}
                    helperText="Leave blank to use the calculated total. Enter a value to lock the seasonal price to it."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Work Time Section */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#ff9800', fontWeight: 'bold' }}>{t('quoteForm.timeLabels.crewWorkTime')}</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.timeLabels.fallCrewWorkTimeMinutes')}
                    value={services.fall.workTimeMinutes}
                    onChange={e => handleServiceChange('fall', 'workTimeMinutes', numVal(e.target.value))}
                    helperText={t('quoteForm.timeLabels.crewWorkTimeHelper')}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Turf Section
  const renderTurfSection = () => {
    const programsBreakdown = turfPricing.breakdown?.programs || {};
    const displayTotal = turfPricing.displayTotal || turfPricing.total || 0;

    return (
      <Box>
        <FormControlLabel
          control={
            <Switch 
              checked={services.turf.enabled}
              onChange={e => handleServiceChange('turf', 'enabled', e.target.checked)}
            />
          }
          label={t('quoteForm.turf.includeTurf', 'Include Turf Programs')}
          sx={{ mb: 3 }}
        />
        
        {services.turf.enabled && (
          <>
            <Box sx={{ mb: 4, p: 3, bgcolor: '#e8f5e8', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32', fontWeight: 'bold' }}>
                {t('quoteForm.turf.suggestedPrograms', 'Suggested Healthy Turf Programs')}
              </Typography>

              <TurfProgramRow
                selected={services.turf.simple3Selected}
                onToggle={e => handleTurfProgramChange('simple3', e.target.checked)}
                label={t('quoteForm.turf.simple3', 'Simple(3) - three applications granular fertilizer')}
                ariaLabel={t('quoteForm.turf.simple3', 'Simple(3) - three applications granular fertilizer')}
                applications={3}
                priceInfo={programsBreakdown.simple3}
                muValue={services.turf.simple3MU}
                onMuChange={e => handleServiceChange('turf', 'simple3MU', numVal(e.target.value, true))}
                muAriaLabel={t('quoteForm.turf.simple3Markup', 'Simple 3 markup percent')}
                t={t}
              />
              <TurfProgramRow
                selected={services.turf.hybrid5Selected}
                onToggle={e => handleTurfProgramChange('hybrid5', e.target.checked)}
                label={t('quoteForm.turf.hybrid5', 'Hybrid(5) - five applications granular/liquid fertilizer')}
                ariaLabel={t('quoteForm.turf.hybrid5', 'Hybrid(5) - five applications granular/liquid fertilizer')}
                applications={5}
                priceInfo={programsBreakdown.hybrid5}
                muValue={services.turf.hybrid5MU}
                onMuChange={e => handleServiceChange('turf', 'hybrid5MU', numVal(e.target.value, true))}
                muAriaLabel={t('quoteForm.turf.hybrid5Markup', 'Hybrid 5 markup percent')}
                t={t}
              />
              <TurfProgramRow
                selected={services.turf.premium6Selected}
                onToggle={e => handleTurfProgramChange('premium6', e.target.checked)}
                label={t('quoteForm.turf.premium6', 'Premium(6) - six applications granular/liquid fertilizer')}
                ariaLabel={t('quoteForm.turf.premium6', 'Premium(6) - six applications granular/liquid fertilizer')}
                applications={6}
                priceInfo={programsBreakdown.premium6}
                muValue={services.turf.premium6MU}
                onMuChange={e => handleServiceChange('turf', 'premium6MU', numVal(e.target.value, true))}
                muAriaLabel={t('quoteForm.turf.premium6Markup', 'Premium 6 markup percent')}
                t={t}
              />
              <TurfProgramRow
                selected={services.turf.ultra6Selected}
                onToggle={e => handleTurfProgramChange('ultra6', e.target.checked)}
                label={t('quoteForm.turf.ultra6', 'Ultra(6) - six applications granular/liquid fertilizer + extras')}
                ariaLabel={t('quoteForm.turf.ultra6', 'Ultra(6) - six applications granular/liquid fertilizer + extras')}
                applications={6}
                priceInfo={programsBreakdown.ultra6}
                muValue={services.turf.ultra6MU}
                onMuChange={e => handleServiceChange('turf', 'ultra6MU', numVal(e.target.value, true))}
                muAriaLabel={t('quoteForm.turf.ultra6Markup', 'Ultra 6 markup percent')}
                t={t}
              />
            </Box>

            <Box sx={{ mb: 4, p: 3, bgcolor: '#f3e5f5', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32', fontWeight: 'bold' }}>
                {t('quoteForm.turf.otherSuggested', 'Other Suggested Healthy Turf Programs')}
              </Typography>
              <Grid container spacing={2}>
                <TurfAddonRow
                  checked={services.turf.grubControl}
                  onToggle={e => handleServiceChange('turf', 'grubControl', e.target.checked)}
                  label={t('quoteForm.turf.grubControl')}
                  ariaLabel={t('quoteForm.turf.grubControl')}
                  price={turfPricing.breakdown?.grubControl?.calculatedPrice || turfPricing.breakdown?.grubControl?.price}
                  overrideValue={services.turf.grubPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'grubPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.grubPrice', 'Grub control seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.aeration}
                  onToggle={e => handleServiceChange('turf', 'aeration', e.target.checked)}
                  label={t('quoteForm.turf.aeration')}
                  ariaLabel={t('quoteForm.turf.aeration')}
                  price={turfPricing.breakdown?.aeration?.calculatedPrice || turfPricing.breakdown?.aeration?.price}
                  overrideValue={services.turf.aerationPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'aerationPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.aerationPrice', 'Aeration seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.soilTest}
                  onToggle={e => handleServiceChange('turf', 'soilTest', e.target.checked)}
                  label={t('quoteForm.turf.soilTest')}
                  ariaLabel={t('quoteForm.turf.soilTest')}
                  price={turfPricing.breakdown?.soilTest?.calculatedPrice || turfPricing.breakdown?.soilTest?.price}
                  overrideValue={services.turf.soilTestPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'soilTestPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.soilTestPrice', 'Soil test seasonal price')}
                  t={t}
                />
              </Grid>
            </Box>
            
            <Box sx={{ mb: 4, p: 3, bgcolor: '#e3f2fd', borderRadius: 2 }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { sm: 'center' },
                gap: 2,
                mb: 3
              }}>
                <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                  {t('quoteForm.turf.specialtyTreatments')}
                </Typography>
                <TextField
                  label={t('quoteForm.turf.overseedMarkup')}
                  type="number"
                  size="small"
                  value={services.turf.overSeedMU}
                  onChange={e => handleServiceChange('turf', 'overSeedMU', numVal(e.target.value, true))}
                  inputProps={{ min: 0, 'aria-label': t('quoteForm.turf.overseedMarkup') }}
                  sx={{ width: { xs: '100%', sm: 180 } }}
                />
              </Box>
              
              <Grid container spacing={2}>
                <TurfAddonRow
                  checked={services.turf.saltStopper}
                  onToggle={e => handleServiceChange('turf', 'saltStopper', e.target.checked)}
                  label={t('quoteForm.turf.saltStopper')}
                  ariaLabel={t('quoteForm.turf.saltStopper')}
                  price={turfPricing.breakdown?.saltStopper?.calculatedPrice || turfPricing.breakdown?.saltStopper?.price}
                  overrideValue={services.turf.saltStopperPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'saltStopperPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.saltStopperPrice', 'Salt Stopper seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.kmag}
                  onToggle={e => handleServiceChange('turf', 'kmag', e.target.checked)}
                  label={t('quoteForm.turf.kmag')}
                  ariaLabel={t('quoteForm.turf.kmag')}
                  price={turfPricing.breakdown?.kmag?.calculatedPrice || turfPricing.breakdown?.kmag?.price}
                  overrideValue={services.turf.kmagPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'kmagPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.kmagPrice', 'K-Mag seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.pHIncreaser}
                  onToggle={e => handleServiceChange('turf', 'pHIncreaser', e.target.checked)}
                  label={t('quoteForm.turf.pHIncreaser')}
                  ariaLabel={t('quoteForm.turf.pHIncreaser')}
                  price={turfPricing.breakdown?.pHIncreaser?.calculatedPrice || turfPricing.breakdown?.pHIncreaser?.price}
                  overrideValue={services.turf.pHIncreaserPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'pHIncreaserPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.pHIncreaserPrice', 'pH Increaser seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.pHDecreaser}
                  onToggle={e => handleServiceChange('turf', 'pHDecreaser', e.target.checked)}
                  label={t('quoteForm.turf.pHDecreaser')}
                  ariaLabel={t('quoteForm.turf.pHDecreaser')}
                  price={turfPricing.breakdown?.pHDecreaser?.calculatedPrice || turfPricing.breakdown?.pHDecreaser?.price}
                  overrideValue={services.turf.pHDecreaserPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'pHDecreaserPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.pHDecreaserPrice', 'pH Decreaser seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.trEliteSeed}
                  onToggle={e => handleServiceChange('turf', 'trEliteSeed', e.target.checked)}
                  label={t('quoteForm.turf.trEliteSeed')}
                  ariaLabel={t('quoteForm.turf.trEliteSeed')}
                  price={turfPricing.breakdown?.trEliteSeed?.calculatedPrice || turfPricing.breakdown?.trEliteSeed?.price}
                  overrideValue={services.turf.trEliteSeedPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'trEliteSeedPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.trEliteSeedPrice', 'TR Elite Seed seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.timeTopdressing}
                  onToggle={e => handleServiceChange('turf', 'timeTopdressing', e.target.checked)}
                  label={t('quoteForm.turf.timeTopdressing')}
                  ariaLabel={t('quoteForm.turf.timeTopdressing')}
                  price={turfPricing.breakdown?.timeTopdressing?.calculatedPrice || turfPricing.breakdown?.timeTopdressing?.price}
                  overrideValue={services.turf.timeTopdressingPrice}
                  onOverrideChange={e => handleServiceChange('turf', 'timeTopdressingPrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.timeTopdressingPrice', 'Topdressing seasonal price')}
                  t={t}
                />
                <TurfAddonRow
                  checked={services.turf.irrigation}
                  onToggle={e => handleServiceChange('turf', 'irrigation', e.target.checked)}
                  label={t('quoteForm.turf.irrigation')}
                  ariaLabel={t('quoteForm.turf.irrigation')}
                  price={turfPricing.breakdown?.irrigation?.calculatedPrice || turfPricing.breakdown?.irrigation?.price}
                  overrideValue={services.turf.irrigationBasePrice}
                  onOverrideChange={e => handleServiceChange('turf', 'irrigationBasePrice', numVal(e.target.value, true))}
                  overrideAriaLabel={t('quoteForm.turf.irrigationPrice', 'Irrigation seasonal price')}
                  t={t}
                />
              </Grid>
            </Box>

            <Box sx={{ mb: 4, p: 3, bgcolor: '#fff8e1', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#f57c00', fontWeight: 'bold' }}>
                {t('quoteForm.turf.disposalFeeSection', 'Disposal Fee')}
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.sweeping.disposalFee', 'Disposal Fee')}
                    value={services.turf.disposalFee}
                    onChange={e => handleServiceChange('turf', 'disposalFee', numVal(e.target.value, true))}
                    inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.turf.disposalFeeAria', 'Fertilizing disposal fee') }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mt: 4, p: 3, bgcolor: '#fff', borderRadius: 2, border: '2px solid #2e7d32' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#2e7d32', fontWeight: 'bold', textAlign: 'center' }}>
                {t('quoteForm.turf.turfProgramTotal', 'Turf Program Total')}
              </Typography>
              
              {turfPricingLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                  <CircularProgress size={24} sx={{ mr: 2, color: '#2e7d32' }} />
                  <Typography variant="body2" color="#2e7d32">{t('quoteForm.turf.calculating', 'Calculating pricing...')}</Typography>
                </Box>
              ) : (
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {t('quoteForm.turf.selectedProgram', 'Selected Program')}: <Chip label={turfPricing.programName} color="primary" />
                    </Typography>
                    {displayTotal > 0 && (
                      <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                        {t('quoteForm.turf.propertySize', 'Property Size')}: {propertyInfo.squareFt || 1000} {t('quoteForm.turf.sqft', 'sq ft')}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: displayTotal > 0 ? '#2e7d32' : '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ 
                        color: displayTotal > 0 ? 'white' : '#999', 
                        fontWeight: 'bold' 
                      }}>
                        {t('quoteForm.turf.totalSeasonalPrice', 'Total Seasonal Price')}
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        color: displayTotal > 0 ? 'white' : '#999', 
                        fontWeight: 'bold' 
                      }}>
                        {formatCurrencyWithSymbol(displayTotal)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Winter Section
  const renderSnowSection = () => {
    const hasManualPrice = (value) => value !== '' && value !== null && value !== undefined && !Number.isNaN(Number(value));

    // Calculate individual service prices based on dynamic rates
    // `ignoreManual` returns the formula price even when a manual override is
    // entered, so the form can show the calculated amount beside each line the
    // way legacy CommercialSnow.cshtml does (lSnowShovelingCitySidewalk etc.).
    const calculateSnowServicePrice = (serviceType, { ignoreManual = false } = {}) => {
      // Helper to safely get rate with fallback
      const getRate = (alias, type = 'fixed', fallback) => {
        if (serviceRates[alias]) {
          // Check for exact type match first
          if (serviceRates[alias][type] !== undefined && serviceRates[alias][type] !== null) {
            return serviceRates[alias][type];
          }
          // Fallback to basic if requested basic/premium but only fixed exists (unlikely given schema but safe)
        }
        return fallback;
      };

      // Get rates - match backend lookup order: try 'basic' first, then 'fixed', then default
      const shovelMinVal = getRate('SnowShovelMinVal', 'basic', null) || getRate('SnowShovelMinVal', 'fixed', null) || 65;
      const sidewalkMinVal = getRate('SnowSidewalkMinVal', 'basic', null) || getRate('SnowSidewalkMinVal', 'fixed', null) || 20;
      const drivewayMinVal = getRate('SnowDrivewayMinVal', 'basic', null) || getRate('SnowDrivewayMinVal', 'fixed', null) || 30;
      const pricePerPlowing = getRate('SnowShovelingCrewPricePerPlowing', 'basic', null) || 130;
      const parkingLotPricePerPlowing = getRate('SnowParkingLotPricePerPlowing', 'basic', null) || 130;
      // Legacy PricesHelper.cs / Quotas.com.js hardcode this divisor
      // (SnowCalculationConstant1 = 60). It is not an Admin Settings rate, so
      // it must not be read from service_prices or quotes drift from legacy.
      const snowCalculationConstant = SNOW_CALCULATION_CONSTANT;
      const lotPlowingEasy = getRate('SnowLotPlowingEasy', 'basic', 65);
      const lotPlowingMedium = getRate('SnowLotPlowingMedium', 'basic', 55);
      const lotPlowingHard = getRate('SnowLotPlowingHard', 'basic', 45);
      const getTariffRate = (tariff) => {
        const normalizedTariff = Number(tariff) || 1;
        switch (normalizedTariff) {
          case 1: return lotPlowingEasy;
          case 2: return lotPlowingMedium;
          case 3: return lotPlowingHard;
          default: return lotPlowingEasy;
        }
      };
      
      switch (serviceType) {
        case 'parkingLotPlowing':
          if (!services.snow.parkingLotParkingLotPlowingEnabled) return 0;
          const plowingRunTime = services.snow.parkingLotPlowingRunningTime || 1;
          const tariffRate = getTariffRate(services.snow.parkingLotPlowingTariff || 1);
          const parkingLotSize = propertyInfo.parkingLotSize || propertyInfo.parkingLot || 0;
          let x1 = (parkingLotSize / tariffRate) * (parkingLotPricePerPlowing / snowCalculationConstant);
          if (x1 < 105) x1 = 105; // Minimum price
          return Math.max(0, x1 * plowingRunTime);
          
        case 'drivewayPlowing':
          if (!services.snow.parkingLotDrivewayPlowingEnabled) return 0;
          return Math.max(0, services.snow.parkingLotDrivewayPlowingPrice || 0);
          
        case 'citySidewalkShoveling': {
          if (!services.snow.shovelingCitySidewalkEnabled) return 0;
          if (!ignoreManual && hasManualPrice(services.snow.shovelingCitySidewalkPrice)) {
            return Math.max(0, Number(services.snow.shovelingCitySidewalkPrice));
          }
          const shovelingRunTime = services.snow.shovelingPlowingRunningTime || 1;
          const citySW = propertyInfo.citySidewalks || 0;
          const internalSW = propertyInfo.internalSidewalks || 0;
          const totalSW = citySW + internalSW;
          if (totalSW === 0) return 0;
          const combined = (totalSW / shovelMinVal) * pricePerPlowing / snowCalculationConstant;
          let x1;
          if (combined < sidewalkMinVal) {
            x1 = sidewalkMinVal * (citySW / totalSW);
          } else {
            x1 = (citySW / shovelMinVal) * pricePerPlowing / snowCalculationConstant;
          }
          return Math.max(0, x1 * shovelingRunTime);
        }
          
        case 'internalSidewalkShoveling': {
          if (!services.snow.shovelingInternalSidewalkEnabled) return 0;
          if (!ignoreManual && hasManualPrice(services.snow.shovelingInternalSidewalkPrice)) {
            return Math.max(0, Number(services.snow.shovelingInternalSidewalkPrice));
          }
          const internalShovelingRunTime = services.snow.shovelingPlowingRunningTime || 1;
          const citySWInt = propertyInfo.citySidewalks || 0;
          const internalSWInt = propertyInfo.internalSidewalks || 0;
          const totalSWInt = citySWInt + internalSWInt;
          if (totalSWInt === 0) return 0;
          const combinedInt = (totalSWInt / shovelMinVal) * pricePerPlowing / snowCalculationConstant;
          let x1Int;
          if (combinedInt < sidewalkMinVal) {
            x1Int = sidewalkMinVal * (internalSWInt / totalSWInt);
          } else {
            x1Int = (internalSWInt / shovelMinVal) * pricePerPlowing / snowCalculationConstant;
          }
          return Math.max(0, x1Int * internalShovelingRunTime);
        }
          
        case 'drivewayShoveling': {
          if (!services.snow.shovelingDrivewayEnabled) return 0;
          if (!ignoreManual && hasManualPrice(services.snow.shovelingDrivewayPrice)) {
            return Math.max(0, Number(services.snow.shovelingDrivewayPrice));
          }
          const drivewayShovelingRunTime = services.snow.shovelingPlowingRunningTime || 1;
          const drivewaySqFt = propertyInfo.drivewaySqFt || 0;
          let x1Dw = (drivewaySqFt / shovelMinVal) * pricePerPlowing / snowCalculationConstant;
          if (x1Dw < drivewayMinVal) x1Dw = drivewayMinVal;
          return Math.max(0, x1Dw * drivewayShovelingRunTime);
        }
          
        default:
          return 0;
      }
    };

    const calculateCrewSaltingEstimate = (sqft, runs, saltPrice, sqftPerMin) => {
      if (!sqft || !runs) return 0;
      const x = Math.ceil(Math.ceil(sqft * 0.000023 * 1560 * 1.05 * runs / 350)) * Math.ceil(saltPrice / 6);
      const y = runs !== 0 ? (sqft / sqftPerMin / 3 * 1.67 * runs) : 0;
      return x + y;
    };

    const calculateTruckParkingLotSaltingEstimate = () => {
      if (!services.snow.truckParkingLotSaltingEnabled) return 0;
      if (hasManualPrice(services.snow.truckParkingLotSaltingPrice)) {
        return Math.max(0, Number(services.snow.truckParkingLotSaltingPrice));
      }
      const pl = propertyInfo.parkingLotSize || propertyInfo.parkingLot || 0;
      const runs = services.snow.parkingLotSaltingRunningTime || serviceRates['SnowTruckDefaultRuns']?.fixed || 30;
      const truckSaltCost = (serviceRates['SnowTruckSaltingCost']?.fixed) || 120;
      const truckLabourCost = (serviceRates['SnowTruckLabourCost']?.fixed) || 65;
      const saltCost = pl * 0.000023 * 900 * 1.15 / 2200 * truckSaltCost;
      const labourCost = pl * 0.000023 * 900 * 1.15 / 50 / 60 * truckLabourCost;
      let x = saltCost + labourCost;
      if (x < 110) x = 110;
      return x * runs;
    };

    const resolveCrewSaltPrice = (formPrice, saltType) => {
      if (formPrice && formPrice !== 50) return formPrice;
      const dbEco = (serviceRates['SnowEcoSaltCost']?.fixed) || 50;
      const dbJet = (serviceRates['SnowJetBlueSaltCost']?.fixed) || 50;
      return (saltType === 'JetBlue') ? dbJet : dbEco;
    };

    // TKT-294: crew salting follows the shoveling crew's run count (~60 per
    // season), not the truck run count (~30 per season).
    const crewSaltingRuns = services.snow.crewSaltingRunningTime
      || serviceRates['SnowShovelCrewRunTime']?.basic
      || serviceRates['SnowShovelCrewRunTime']?.fixed
      || 60;
    const crewSaltingSqftPerMin = (field) => services.snow[field] || serviceRates['SnowSaltingCrewSqftPerMin']?.fixed || 65;

    const crewCitySaltingEst = services.snow.crewCitySidewalkSaltingEnabled ?
      calculateCrewSaltingEstimate(propertyInfo.citySidewalks || 0, crewSaltingRuns, resolveCrewSaltPrice(services.snow.saltingCrewCitySideWalksSaltPrice, services.snow.saltingCrewCitySideWalksSaltType), crewSaltingSqftPerMin('saltingCrewSqftPerMinCitySideWalks')) : 0;
    const crewInternalSaltingEst = services.snow.crewInternalSidewalkSaltingEnabled ?
      calculateCrewSaltingEstimate(propertyInfo.internalSidewalks || 0, crewSaltingRuns, resolveCrewSaltPrice(services.snow.saltingCrewInternalSideWalksSaltPrice, services.snow.saltingCrewInternalSideWalksSaltType), crewSaltingSqftPerMin('saltingCrewSqftPerMinInternalSidewalks')) : 0;
    // TKT-295: a manually entered driveway salting price wins over the estimate.
    const crewDrivewaySaltingEst = !services.snow.crewDrivewaySaltingEnabled ? 0
      : hasManualPrice(services.snow.saltingCrewDrivewaysPrice)
        ? Math.max(0, Number(services.snow.saltingCrewDrivewaysPrice))
        : calculateCrewSaltingEstimate(propertyInfo.drivewaySqFt || 0, crewSaltingRuns, resolveCrewSaltPrice(services.snow.saltingCrewDrivewaysSaltPrice, services.snow.saltingCrewDrivewaysSaltType), crewSaltingSqftPerMin('saltingCrewSqftPerMinDriveways'));
    const truckParkingLotSaltingEst = calculateTruckParkingLotSaltingEstimate();
    const truckDrivewaySaltingEst = services.snow.truckDrivewaySaltingEnabled ? (services.snow.truckDrivewaySaltingPrice || 0) : 0;

    // Winter trash bags - legacy formula (Quotas.com.js calcSnowTrashBags /
    // backend calculateTrashBagPrice): first bag = per-bag price x times/week
    // x 0.75, each extra bag at the extra-bag rate. Mirrors the backend so the
    // form can show the calculated amount instead of an empty override box.
    const snowGeneralRate = (alias, fallback) => {
      const rate = serviceRates[alias];
      return rate?.basic ?? rate?.fixed ?? fallback;
    };
    const calculateSnowTrashBagEstimate = () => {
      const qty = Number(services.snow.trashBagQuantity) || 0;
      const perWeek = Number(services.snow.trashBagTimePerWeek) || 0;
      if (qty <= 0 || perWeek <= 0) return 0;
      const onSite = (services.snow.trashBagType || 'OnSite') === 'OnSite';
      const basePrice = onSite
        ? snowGeneralRate('GeneralQuotePricePerBagDisposedOnSite', 35)
        : snowGeneralRate('GeneralQuotePricePerBagDisposedOffSite', 55);
      const extraPrice = onSite
        ? snowGeneralRate('GeneralQuotePriceExtraBagDisposedOnSite', 8)
        : snowGeneralRate('GeneralQuotePriceExtraBagDisposedOffSite', 20);
      let total = basePrice * perWeek * 0.75;
      if (qty > 1) total += extraPrice * (qty - 1);
      else total *= qty;
      return Math.round(total * 100) / 100;
    };
    const snowTrashBagCalculated = calculateSnowTrashBagEstimate();
    const snowTrashBagEst = !services.snow.trashBagEnabled ? 0
      : hasManualPrice(services.snow.trashBagPrice)
        ? Math.max(0, Number(services.snow.trashBagPrice))
        : snowTrashBagCalculated;
    const saltBinsEst = services.snow.saltBinsEnabled
      ? (Number(services.snow.saltBinsFinalPrice) > 0
        ? Number(services.snow.saltBinsFinalPrice)
        : (Number(services.snow.saltBins) || 0) * ((serviceRates['SnowSaltingPricePerBins']?.fixed) || 125))
      : 0;
    const premiumSaltingEst = services.snow.premiumSaltingEnabled ? (Number(services.snow.premiumSaltingPrice) || 0) : 0;
    const extrasEstimate = snowTrashBagEst + saltBinsEst + premiumSaltingEst;

    const plowingEstimate = calculateSnowServicePrice('parkingLotPlowing') + calculateSnowServicePrice('drivewayPlowing');
    // Formula prices per shoveling line (legacy shows these read-only next to
    // each checkbox). Displayed beside the line and used as the placeholder of
    // the manual-override box so the box is never a meaningless blank.
    const cityShovelingCalculated = calculateSnowServicePrice('citySidewalkShoveling', { ignoreManual: true });
    const internalShovelingCalculated = calculateSnowServicePrice('internalSidewalkShoveling', { ignoreManual: true });
    const drivewayShovelingCalculated = calculateSnowServicePrice('drivewayShoveling', { ignoreManual: true });
    const shovelingEstimate = calculateSnowServicePrice('citySidewalkShoveling') +
                              calculateSnowServicePrice('internalSidewalkShoveling') +
                              calculateSnowServicePrice('drivewayShoveling');
    const saltingEstimate = truckParkingLotSaltingEst + truckDrivewaySaltingEst + crewCitySaltingEst + crewInternalSaltingEst + crewDrivewaySaltingEst;

    // Shared bits for the three shoveling override boxes: the line's effective
    // price (manual if entered, else formula) and the helper text that tells
    // the rep what the formula produced.
    const shovelingLinePrice = (manual, calculated) =>
      hasManualPrice(manual) ? Math.max(0, Number(manual)) : calculated;
    const shovelingPriceHelper = (calculated) =>
      t('quoteForm.snow.calculatedPriceHelper', 'Calculated: {{amount}}. Enter a value to override.', {
        amount: formatCurrencyWithSymbol(calculated)
      });

    let plowingTotal = pricing?.snow?.plowingTotal ?? plowingEstimate;
    let shovelingTotal = pricing?.snow?.shovelingTotal ?? shovelingEstimate;
    let saltingTotal = pricing?.snow?.saltingTotal ?? saltingEstimate;

    const calculatePerEventAutoPrice = () => {
      const truckRuns = services.snow.parkingLotSaltingRunningTime || 0;
      const crewRuns = services.snow.crewSaltingRunningTime || 0;
      const truckTotal = truckParkingLotSaltingEst + truckDrivewaySaltingEst;
      const crewTotal = crewCitySaltingEst + crewInternalSaltingEst + crewDrivewaySaltingEst;
      let perEvent = 0;
      if (truckRuns > 0 && truckTotal > 0) perEvent += truckTotal / truckRuns;
      if (crewRuns > 0 && crewTotal > 0) perEvent += crewTotal / crewRuns;
      return Math.round(perEvent * 100) / 100;
    };

    // Enforce seasonal minimums per category
    const getRate = (alias, type = 'fixed', fallback) => {
      if (serviceRates[alias]?.[type] !== undefined && serviceRates[alias]?.[type] !== null) {
        return serviceRates[alias][type];
      }
      return fallback;
    };
    const shovelingMinSeasonal = getRate('SnowShovelingMinSeasonal', 'fixed', 0);
    const plowingMinSeasonal = getRate('SnowPlowingMinSeasonal', 'fixed', 0);
    const saltingMinSeasonal = getRate('SnowSaltingMinSeasonal', 'fixed', 0);

    const shovelingBelowMin = shovelingMinSeasonal > 0 && shovelingTotal > 0 && shovelingTotal < shovelingMinSeasonal;
    const plowingBelowMin = plowingMinSeasonal > 0 && plowingTotal > 0 && plowingTotal < plowingMinSeasonal;
    const saltingBelowMin = saltingMinSeasonal > 0 && saltingTotal > 0 && saltingTotal < saltingMinSeasonal;

    if (shovelingBelowMin) shovelingTotal = shovelingMinSeasonal;
    if (plowingBelowMin) plowingTotal = plowingMinSeasonal;
    if (saltingBelowMin) saltingTotal = saltingMinSeasonal;
    
    // Use backend total if available, otherwise fall back to frontend estimate.
    // The backend total includes extras (trash bags, salt bins, premium
    // salting), so the fallback must too or the total jumps on save.
    const totalSnowPrice = pricing?.snow?.total || (plowingTotal + shovelingTotal + saltingTotal + extrasEstimate);

    return (
      <Box>
        <FormControlLabel
          control={
            <Switch 
              checked={services.snow.enabled}
              onChange={e => handleServiceChange('snow', 'enabled', e.target.checked)}
            />
          }
          label="Include Snow Removal Services"
          sx={{ mb: 3 }}
        />
        
        {/* TKT-228 (Doug §5.A): always render Snow Removal's options, same
            as the accordion being expanded by default - sales staff should
            see every option immediately rather than needing to flip this
            switch first. The switch above still controls whether Snow is
            actually included when the quote is saved/priced. */}
        <>
            {/* Program Selection — residential package only.
                Commercial, multi-residential, and industrial quotes use the
                commercial snow sections below. Construction still shows both
                until that split is defined. */}
            {showResidentialSnowPackages && (
            <Box sx={{ mb: 4, p: 3, bgcolor: '#e3f2fd', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#2196f3', fontWeight: 'bold' }}>
                Snow Removal and Maintenance
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  { value: 1, label: t('quoteForm.snow.basicProgram', 'Basic Program') },
                  { value: 2, label: t('quoteForm.snow.premiumProgram', 'Premium Program') },
                  { value: 3, label: t('quoteForm.snow.premiumSaltProgram', 'Premium + Salt') },
                ].map(({ value, label }) => (
                  <Grid key={value} size={{ xs: 12, sm: 4, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={services.snow.program === value}
                          onChange={e => handleSnowProgramChange(value, e.target.checked)}
                          inputProps={{ 'aria-label': label }}
                        />
                      }
                      label={label}
                    />
                  </Grid>
                ))}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Total Seasonal Price</Typography>
                    <Typography variant="h6" color="primary.main">{formatCurrencyWithSymbol(totalSnowPrice)}</Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {services.snow.program > 0 && isResidential && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={{
                        1: t('quoteForm.snow.basicBasePrice', 'Snow Base Price (Basic)'),
                        2: t('quoteForm.snow.premiumBasePrice', 'Snow Base Price (Premium)'),
                        3: t('quoteForm.snow.premiumSaltBasePrice', 'Snow Base Price (Premium + Salt)'),
                      }[services.snow.program]}
                      type="number"
                      value={services.snow.baseSum || ''}
                      onChange={e => handleServiceChange('snow', 'baseSum', numVal(e.target.value, true))}
                      InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                      inputProps={{ 'aria-label': 'Snow base price' }}
                      size="small"
                      helperText={t('quoteForm.snow.basePriceHelper', 'Base seasonal price for snow removal program')}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
            )}

            {/* Plowing Services. TKT-228: drop the program gate for
                commercial quotes so all sub-option blocks render the
                moment Snow is enabled (matches Summer section UX).
                Residential keeps the program gate because the
                residential model needs the Basic/Premium tier first. */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#fff3e0', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#2196f3', fontWeight: 'bold' }}>
                  Plowing Services
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.parkingLotDrivewayPlowingEnabled}
                          onChange={e => handleServiceChange('snow', 'parkingLotDrivewayPlowingEnabled', e.target.checked)}
                          aria-label={t('quoteForm.snow.drivewayPlowing', 'Enable driveway plowing')}
                        />
                      }
                      label={t('quoteForm.snow.drivewayPlowing', 'Driveways')}
                    />
                    {services.snow.parkingLotDrivewayPlowingEnabled && (
                      <TextField
                        label={t('quoteForm.snow.drivewayPlowingPrice', 'Driveway Plowing Price')}
                        type="number"
                        size="small"
                        value={services.snow.parkingLotDrivewayPlowingPrice}
                        onChange={e => handleServiceChange('snow', 'parkingLotDrivewayPlowingPrice', numVal(e.target.value, true))}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.drivewayPlowingPrice', 'Driveway plowing price') }}
                        sx={{ mt: 1, ml: 4 }}
                      />
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.parkingLotParkingLotPlowingEnabled}
                          onChange={e => handleServiceChange('snow', 'parkingLotParkingLotPlowingEnabled', e.target.checked)}
                          aria-label={t('quoteForm.snow.parkingLotPlowing', 'Enable parking lot plowing')}
                        />
                      }
                      label={t('quoteForm.snow.parkingLotPlowing', 'Parking Lot')}
                    />
                    {services.snow.parkingLotParkingLotPlowingEnabled && (
                      <Typography variant="body2" color="success.main" sx={{ ml: 4 }}>
                        {t('quoteForm.snow.price', 'Price')}: {formatCurrencyWithSymbol(calculateSnowServicePrice('parkingLotPlowing'))}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.plowingRunTimes', 'Plowing - Run Times')}
                      value={services.snow.parkingLotPlowingRunningTime}
                      onChange={e => handleServiceChange('snow', 'parkingLotPlowingRunningTime', numVal(e.target.value))}
                      helperText={t('quoteForm.snow.plowingRunTimesHelper', 'Run time value used in seasonal price calculation')}
                      inputProps={{ 'aria-label': t('quoteForm.snow.plowingRunTimes', 'Plowing - Run Times') }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SnowPricingTotalBox
                      label={t('quoteForm.snow.plowingTotal', 'Plowing Total')}
                      value={plowingTotal}
                      belowMin={plowingBelowMin}
                      minSeasonal={plowingMinSeasonal}
                      t={t}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Shoveling Services */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#2196f3', fontWeight: 'bold' }}>
                  Shoveling Services
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.shovelingCitySidewalkEnabled}
                          onChange={e => handleServiceChange('snow', 'shovelingCitySidewalkEnabled', e.target.checked)}
                        />
                      }
                      label="City Sidewalk Shoveling"
                    />
                    {services.snow.shovelingCitySidewalkEnabled && (
                      <Typography variant="body2" color="success.main" sx={{ ml: 4 }} aria-live="polite">
                        {formatCurrencyWithSymbol(shovelingLinePrice(services.snow.shovelingCitySidewalkPrice, cityShovelingCalculated))}
                        {!hasManualPrice(services.snow.shovelingCitySidewalkPrice) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('quoteForm.snow.calculatedLabel', 'calculated')})
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {services.snow.shovelingCitySidewalkEnabled && (
                      <TextField
                        label={t('quoteForm.snow.citySidewalkShovelingPrice', 'City Sidewalk Shoveling Price')}
                        type="number"
                        size="small"
                        value={services.snow.shovelingCitySidewalkPrice}
                        onChange={e => handleServiceChange('snow', 'shovelingCitySidewalkPrice', numVal(e.target.value, true))}
                        placeholder={cityShovelingCalculated.toFixed(2)}
                        helperText={shovelingPriceHelper(cityShovelingCalculated)}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.citySidewalkShovelingPrice', 'City sidewalk shoveling price') }}
                        sx={{ mt: 1, ml: 4, width: { xs: 'calc(100% - 32px)', sm: 220 } }}
                      />
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.shovelingInternalSidewalkEnabled}
                          onChange={e => handleServiceChange('snow', 'shovelingInternalSidewalkEnabled', e.target.checked)}
                        />
                      }
                      label="Internal Sidewalk Shoveling"
                    />
                    {services.snow.shovelingInternalSidewalkEnabled && (
                      <Typography variant="body2" color="success.main" sx={{ ml: 4 }} aria-live="polite">
                        {formatCurrencyWithSymbol(shovelingLinePrice(services.snow.shovelingInternalSidewalkPrice, internalShovelingCalculated))}
                        {!hasManualPrice(services.snow.shovelingInternalSidewalkPrice) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('quoteForm.snow.calculatedLabel', 'calculated')})
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {services.snow.shovelingInternalSidewalkEnabled && (
                      <TextField
                        label={t('quoteForm.snow.internalSidewalkShovelingPrice', 'Internal Sidewalk Shoveling Price')}
                        type="number"
                        size="small"
                        value={services.snow.shovelingInternalSidewalkPrice}
                        onChange={e => handleServiceChange('snow', 'shovelingInternalSidewalkPrice', numVal(e.target.value, true))}
                        placeholder={internalShovelingCalculated.toFixed(2)}
                        helperText={shovelingPriceHelper(internalShovelingCalculated)}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.internalSidewalkShovelingPrice', 'Internal sidewalk shoveling price') }}
                        sx={{ mt: 1, ml: 4, width: { xs: 'calc(100% - 32px)', sm: 220 } }}
                      />
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.shovelingDrivewayEnabled}
                          onChange={e => handleServiceChange('snow', 'shovelingDrivewayEnabled', e.target.checked)}
                        />
                      }
                      label="Driveway Shoveling"
                    />
                    {services.snow.shovelingDrivewayEnabled && (
                      <Typography variant="body2" color="success.main" sx={{ ml: 4 }} aria-live="polite">
                        {formatCurrencyWithSymbol(shovelingLinePrice(services.snow.shovelingDrivewayPrice, drivewayShovelingCalculated))}
                        {!hasManualPrice(services.snow.shovelingDrivewayPrice) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('quoteForm.snow.calculatedLabel', 'calculated')})
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {services.snow.shovelingDrivewayEnabled && (
                      <TextField
                        label={t('quoteForm.snow.drivewayShovelingPrice', 'Driveway Shoveling Price')}
                        type="number"
                        size="small"
                        value={services.snow.shovelingDrivewayPrice}
                        onChange={e => handleServiceChange('snow', 'shovelingDrivewayPrice', numVal(e.target.value, true))}
                        placeholder={drivewayShovelingCalculated.toFixed(2)}
                        helperText={shovelingPriceHelper(drivewayShovelingCalculated)}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.drivewayShovelingPrice', 'Driveway shoveling price') }}
                        sx={{ mt: 1, ml: 4, width: { xs: 'calc(100% - 32px)', sm: 220 } }}
                      />
                    )}
                  </Grid>
                </Grid>
                
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.shovelingRunTimes', 'Shoveling Crew - Run Times')}
                      value={services.snow.shovelingPlowingRunningTime}
                      onChange={e => handleServiceChange('snow', 'shovelingPlowingRunningTime', numVal(e.target.value))}
                      helperText={t('quoteForm.snow.shovelingRunTimesHelper', 'Crew time in minutes per visit (used in seasonal price calculation)')}
                      inputProps={{ 'aria-label': t('quoteForm.snow.shovelingRunTimes', 'Shoveling Crew - Run Times') }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SnowPricingTotalBox
                      label={t('quoteForm.snow.shovelingTotal', 'Shoveling Total')}
                      value={shovelingTotal}
                      belowMin={shovelingBelowMin}
                      minSeasonal={shovelingMinSeasonal}
                      t={t}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tariff Settings */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#e8eaf6', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#3f51b5', fontWeight: 'bold' }}>
                  Difficulty Settings
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="plowing-tariff-label">Plowing Difficulty</InputLabel>
                      <Select
                        labelId="plowing-tariff-label"
                        value={services.snow.parkingLotPlowingTariff}
                        onChange={e => handleServiceChange('snow', 'parkingLotPlowingTariff', e.target.value)}
                        label="Plowing Difficulty"
                        inputProps={{ 'aria-label': 'Select plowing difficulty level' }}
                      >
                        <MenuItem value={1}>Easy (Standard Rate)</MenuItem>
                        <MenuItem value={2}>Medium (Higher Rate)</MenuItem>
                        <MenuItem value={3}>Hard (Highest Rate)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Salting Services - Truck */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#fff8e1', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#f57c00', fontWeight: 'bold' }}>
                  {t('quoteForm.snow.truckSaltingTitle', 'Salting - Parking Lots')}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.truckParkingLotSaltingEnabled}
                          onChange={e => handleServiceChange('snow', 'truckParkingLotSaltingEnabled', e.target.checked)}
                          aria-label={t('quoteForm.snow.truckParkingLotSalting', 'Enable truck parking lot salting')}
                        />
                      }
                      label={t('quoteForm.snow.parkingLotLabel', 'Parking Lot')}
                    />
                    {/* Show the calculated seasonal price next to the checkbox
                        (same treatment as the crew salting lines) so the line
                        never reads as "no calculated price" when the manual
                        override box is left blank. */}
                    {services.snow.truckParkingLotSaltingEnabled && (
                      <Typography variant="body2" color="success.main" sx={{ ml: 4 }} aria-live="polite">
                        {formatCurrencyWithSymbol(truckParkingLotSaltingEst)}
                        {!hasManualPrice(services.snow.truckParkingLotSaltingPrice) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('quoteForm.snow.calculatedLabel', 'calculated')})
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {services.snow.truckParkingLotSaltingEnabled && (
                      <TextField
                        label={t('quoteForm.snow.parkingLotTruckSaltingPrice', 'Parking Lot Truck Salting Price')}
                        type="number"
                        size="small"
                        value={services.snow.truckParkingLotSaltingPrice}
                        onChange={e => handleServiceChange('snow', 'truckParkingLotSaltingPrice', numVal(e.target.value, true))}
                        helperText={t('quoteForm.snow.manualPriceHelper', 'Leave blank to use the calculated price.')}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.parkingLotTruckSaltingPrice', 'Parking lot truck salting price') }}
                        sx={{ mt: 1, ml: 4, width: { xs: 'calc(100% - 32px)', sm: 240 } }}
                      />
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.truckDrivewaySaltingEnabled}
                          onChange={e => handleServiceChange('snow', 'truckDrivewaySaltingEnabled', e.target.checked)}
                          aria-label={t('quoteForm.snow.truckDrivewaySalting', 'Enable truck driveway salting')}
                        />
                      }
                      label={t('quoteForm.snow.drivewaysLabel', 'Driveways')}
                    />
                    {services.snow.truckDrivewaySaltingEnabled && (
                      <TextField
                        label={t('quoteForm.snow.drivewaySaltingPrice', 'Driveway Salting Price')}
                        type="number"
                        size="small"
                        value={services.snow.truckDrivewaySaltingPrice}
                        onChange={e => handleServiceChange('snow', 'truckDrivewaySaltingPrice', numVal(e.target.value, true))}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.drivewaySaltingPrice', 'Truck driveway salting price') }}
                        sx={{ mt: 1, ml: 4 }}
                      />
                    )}
                  </Grid>
                </Grid>
                
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.truckSaltingRunTimes', 'Run Times')}
                      value={services.snow.parkingLotSaltingRunningTime}
                      onChange={e => handleServiceChange('snow', 'parkingLotSaltingRunningTime', numVal(e.target.value))}
                      inputProps={{ 'aria-label': t('quoteForm.snow.truckSaltingRunTimes', 'Truck salting run times') }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Salting Services - Crew */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#e0f2f1', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#00796b', fontWeight: 'bold' }}>
                  {t('quoteForm.snow.crewSaltingTitle', 'Salting - Shoveling Crew')}
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.crewSaltingRunTimes', 'Run Times')}
                      value={services.snow.crewSaltingRunningTime}
                      onChange={e => handleServiceChange('snow', 'crewSaltingRunningTime', numVal(e.target.value))}
                      inputProps={{ 'aria-label': t('quoteForm.snow.crewSaltingRunTimes', 'Crew salting run times') }}
                    />
                  </Grid>
                </Grid>

                {/* City Sidewalks */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={services.snow.crewCitySidewalkSaltingEnabled}
                            onChange={e => handleServiceChange('snow', 'crewCitySidewalkSaltingEnabled', e.target.checked)}
                            aria-label={t('quoteForm.snow.crewCitySidewalkSalting', 'Enable crew city sidewalk salting')}
                          />
                        }
                        label={t('quoteForm.snow.citySidewalksLabel', 'City Sidewalks')}
                      />
                      {services.snow.crewCitySidewalkSaltingEnabled && (
                        <Typography variant="body2" color="success.main" sx={{ ml: 4 }}>
                          {formatCurrencyWithSymbol(crewCitySaltingEst)}
                        </Typography>
                      )}
                    </Grid>
                    {services.snow.crewCitySidewalkSaltingEnabled && (
                      <>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="city-salt-type-label">{t('quoteForm.snow.saltType', 'Salt Type')}</InputLabel>
                            <Select
                              labelId="city-salt-type-label"
                              value={services.snow.saltingCrewCitySideWalksSaltType || 'EcoSalt'}
                              onChange={e => {
                                const saltType = e.target.value;
                                const jetBluePrice = (serviceRates['SnowJetBlueSaltCost']?.fixed) || 50;
                                const ecoSaltPrice = (serviceRates['SnowEcoSaltCost']?.fixed) || 50;
                                handleServiceChange('snow', 'saltingCrewCitySideWalksSaltType', saltType);
                                handleServiceChange('snow', 'saltingCrewCitySideWalksSaltPrice', saltType === 'JetBlue' ? jetBluePrice : ecoSaltPrice);
                              }}
                              label={t('quoteForm.snow.saltType', 'Salt Type')}
                              inputProps={{ 'aria-label': t('quoteForm.snow.citySidewalkSaltType', 'City sidewalk salt type') }}
                            >
                              <MenuItem value="JetBlue">Jet Blue</MenuItem>
                              <MenuItem value="EcoSalt">Eco Salt</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('quoteForm.snow.sqftPerMin', 'Sqft/min')}
                            value={services.snow.saltingCrewSqftPerMinCitySideWalks}
                            onChange={e => handleServiceChange('snow', 'saltingCrewSqftPerMinCitySideWalks', numVal(e.target.value))}
                            inputProps={{ min: 1, 'aria-label': t('quoteForm.snow.citySidewalkSqftPerMin', 'City sidewalk sqft per min') }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>

                {/* Internal Sidewalks */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={services.snow.crewInternalSidewalkSaltingEnabled}
                            onChange={e => handleServiceChange('snow', 'crewInternalSidewalkSaltingEnabled', e.target.checked)}
                            aria-label={t('quoteForm.snow.crewInternalSidewalkSalting', 'Enable crew internal sidewalk salting')}
                          />
                        }
                        label={t('quoteForm.snow.internalSidewalksLabel', 'Internal Sidewalks')}
                      />
                      {services.snow.crewInternalSidewalkSaltingEnabled && (
                        <Typography variant="body2" color="success.main" sx={{ ml: 4 }}>
                          {formatCurrencyWithSymbol(crewInternalSaltingEst)}
                        </Typography>
                      )}
                    </Grid>
                    {services.snow.crewInternalSidewalkSaltingEnabled && (
                      <>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="internal-salt-type-label">{t('quoteForm.snow.saltType', 'Salt Type')}</InputLabel>
                            <Select
                              labelId="internal-salt-type-label"
                              value={services.snow.saltingCrewInternalSideWalksSaltType || 'EcoSalt'}
                              onChange={e => {
                                const saltType = e.target.value;
                                const jetBluePrice = (serviceRates['SnowJetBlueSaltCost']?.fixed) || 50;
                                const ecoSaltPrice = (serviceRates['SnowEcoSaltCost']?.fixed) || 50;
                                handleServiceChange('snow', 'saltingCrewInternalSideWalksSaltType', saltType);
                                handleServiceChange('snow', 'saltingCrewInternalSideWalksSaltPrice', saltType === 'JetBlue' ? jetBluePrice : ecoSaltPrice);
                              }}
                              label={t('quoteForm.snow.saltType', 'Salt Type')}
                              inputProps={{ 'aria-label': t('quoteForm.snow.internalSidewalkSaltType', 'Internal sidewalk salt type') }}
                            >
                              <MenuItem value="JetBlue">Jet Blue</MenuItem>
                              <MenuItem value="EcoSalt">Eco Salt</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('quoteForm.snow.sqftPerMin', 'Sqft/min')}
                            value={services.snow.saltingCrewSqftPerMinInternalSidewalks}
                            onChange={e => handleServiceChange('snow', 'saltingCrewSqftPerMinInternalSidewalks', numVal(e.target.value))}
                            inputProps={{ min: 1, 'aria-label': t('quoteForm.snow.internalSidewalkSqftPerMin', 'Internal sidewalk sqft per min') }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>

                {/* Driveways */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={services.snow.crewDrivewaySaltingEnabled}
                            onChange={e => handleServiceChange('snow', 'crewDrivewaySaltingEnabled', e.target.checked)}
                            aria-label={t('quoteForm.snow.crewDrivewaySalting', 'Enable crew driveway salting')}
                          />
                        }
                        label={t('quoteForm.snow.drivewaysLabel', 'Driveways')}
                      />
                      {services.snow.crewDrivewaySaltingEnabled && (
                        <Typography variant="body2" color="success.main" sx={{ ml: 4 }}>
                          {formatCurrencyWithSymbol(crewDrivewaySaltingEst)}
                        </Typography>
                      )}
                    </Grid>
                    {services.snow.crewDrivewaySaltingEnabled && (
                      <>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="driveway-salt-type-label">{t('quoteForm.snow.saltType', 'Salt Type')}</InputLabel>
                            <Select
                              labelId="driveway-salt-type-label"
                              value={services.snow.saltingCrewDrivewaysSaltType || 'EcoSalt'}
                              onChange={e => {
                                const saltType = e.target.value;
                                const jetBluePrice = (serviceRates['SnowJetBlueSaltCost']?.fixed) || 50;
                                const ecoSaltPrice = (serviceRates['SnowEcoSaltCost']?.fixed) || 50;
                                handleServiceChange('snow', 'saltingCrewDrivewaysSaltType', saltType);
                                handleServiceChange('snow', 'saltingCrewDrivewaysSaltPrice', saltType === 'JetBlue' ? jetBluePrice : ecoSaltPrice);
                              }}
                              label={t('quoteForm.snow.saltType', 'Salt Type')}
                              inputProps={{ 'aria-label': t('quoteForm.snow.drivewaySaltType', 'Driveway salt type') }}
                            >
                              <MenuItem value="JetBlue">Jet Blue</MenuItem>
                              <MenuItem value="EcoSalt">Eco Salt</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('quoteForm.snow.sqftPerMin', 'Sqft/min')}
                            value={services.snow.saltingCrewSqftPerMinDriveways}
                            onChange={e => handleServiceChange('snow', 'saltingCrewSqftPerMinDriveways', numVal(e.target.value))}
                            inputProps={{ min: 1, 'aria-label': t('quoteForm.snow.drivewaySqftPerMin', 'Driveway sqft per min') }}
                          />
                        </Grid>
                      </>
                    )}
                    {/* TKT-295: Driveways had no price box at all. It follows the
                        manual-override pattern from sidewalk shoveling and parking
                        lot truck salting, and stays visible without ticking the
                        checkbox so the line item doesn't read as price-less. */}
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('quoteForm.snow.drivewayCrewSaltingPrice', 'Driveway Salting Price')}
                        value={services.snow.saltingCrewDrivewaysPrice}
                        onChange={e => handleServiceChange('snow', 'saltingCrewDrivewaysPrice', numVal(e.target.value, true))}
                        helperText={t('quoteForm.snow.manualPriceHelper', 'Leave blank to use the calculated price.')}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.drivewayCrewSaltingPrice', 'Driveway crew salting price') }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Salt Bins Cost */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={services.snow.saltBinsEnabled}
                            onChange={e => handleServiceChange('snow', 'saltBinsEnabled', e.target.checked)}
                            aria-label={t('quoteForm.snow.saltBinsCost', 'Enable salt bins cost')}
                          />
                        }
                        label={t('quoteForm.snow.saltBinsCost', 'Salt Bins Cost')}
                      />
                    </Grid>
                    {/* TKT-295: these were hidden until the checkbox was ticked,
                        so Salt Bin Cost read as having no price box. Always
                        visible now; the checkbox still controls whether the
                        bins are charged on the quote. */}
                    <Grid size={{ xs: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('quoteForm.snow.numberOfBins', '# of Bins')}
                        value={services.snow.saltBins}
                        onChange={e => {
                          const bins = numVal(e.target.value);
                          const pricePerBin = (serviceRates['SnowSaltingPricePerBins']?.fixed) || 125;
                          handleServiceChange('snow', 'saltBins', bins);
                          // Legacy (Quotas.com.js calcSnowCrewSalting) recomputes
                          // price-per-bin x bins on every change. The old guard only
                          // set the price when it was empty, so 1 bin -> $400 then
                          // 3 bins stayed at $400. The price box below stays editable
                          // for a manual override after the count is set.
                          handleServiceChange('snow', 'saltBinsFinalPrice', bins * pricePerBin);
                        }}
                        inputProps={{ min: 0, 'aria-label': t('quoteForm.snow.numberOfBins', 'Number of bins') }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('quoteForm.snow.saltBinsPrice', 'Salt Bins Price')}
                        value={services.snow.saltBinsFinalPrice || (services.snow.saltBins * ((serviceRates['SnowSaltingPricePerBins']?.fixed) || 125))}
                        onChange={e => handleServiceChange('snow', 'saltBinsFinalPrice', numVal(e.target.value, true))}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.saltBinsPrice', 'Salt bins price') }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            {/* Premium Snow Maintenance */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#fce4ec', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#c2185b', fontWeight: 'bold' }}>
                  {t('quoteForm.snow.premiumSnowMaintenance', 'Premium Snow Maintenance')}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.premiumSaltingEnabled}
                          onChange={e => {
                            const checked = e.target.checked;
                            const defaultPrice = serviceRates['SnowPremiumMaintenanceDefaultPrice']?.basic
                              || serviceRates['SnowPremiumMaintenanceDefaultPrice']?.fixed
                              || 2500;
                            setServices(prev => ({
                              ...prev,
                              snow: {
                                ...prev.snow,
                                premiumSaltingEnabled: checked,
                                ...(checked && isCommercial && !(Number(prev.snow.premiumSaltingPrice) > 0)
                                  ? { premiumSaltingPrice: defaultPrice }
                                  : {})
                              }
                            }));
                          }}
                          aria-label={t('quoteForm.snow.premiumSnowMaintenance', 'Enable premium snow maintenance')}
                        />
                      }
                      label={t('quoteForm.snow.premiumSnowMaintenance', 'Premium Snow Maintenance')}
                    />
                    {services.snow.premiumSaltingEnabled && (
                      <TextField
                        label={t('quoteForm.snow.premiumPrice', 'Seasonal Price')}
                        type="number"
                        size="small"
                        value={services.snow.premiumSaltingPrice}
                        onChange={e => handleServiceChange('snow', 'premiumSaltingPrice', numVal(e.target.value, true))}
                        InputProps={{ startAdornment: '$' }}
                        inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.premiumPrice', 'Premium snow maintenance price') }}
                        sx={{ mt: 1 }}
                        fullWidth
                      />
                    )}
                  </Grid>
                </Grid>
                {/* TKT-229: Salt Application total uses the same pricing box as
                    Plowing/Shoveling. saltingTotal already combines truck +
                    crew salting above. */}
                <Grid container spacing={2} sx={{ mt: 1 }} justifyContent="flex-end">
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SnowPricingTotalBox
                      label={t('quoteForm.snow.saltingTotal', 'Salt Application Total')}
                      value={saltingTotal}
                      belowMin={saltingBelowMin}
                      minSeasonal={saltingMinSeasonal}
                      t={t}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Salt Applications Per Event */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#e8eaf6', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#3f51b5', fontWeight: 'bold' }}>
                  {t('quoteForm.snow.saltApplicationsPerEvent', 'Salt Applications Per Event')}
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.perEventEnabled}
                          onChange={e => {
                            handleServiceChange('snow', 'perEventEnabled', e.target.checked);
                            if (e.target.checked && !services.snow.isCustomPerEventPrice) {
                              handleServiceChange('snow', 'perEventPrice', calculatePerEventAutoPrice());
                            }
                          }}
                          aria-label={t('quoteForm.snow.saltApplicationsPerEvent', 'Enable salt applications per event')}
                        />
                      }
                      label={t('quoteForm.snow.saltApplicationsPerEvent', 'Salt Applications Per Event')}
                    />
                  </Grid>
                  {/* TKT-296: the price box used to appear only after ticking the
                      checkbox, so the line item read as having no price field at
                      all. It's always visible now; the checkbox still controls
                      whether the per-event price applies to the quote. */}
                  <Grid size={{ xs: 8, md: 4 }}>
                    <TextField
                      label={t('quoteForm.snow.perEventPrice', 'Per Event Price')}
                      type="number"
                      size="small"
                      fullWidth
                      value={services.snow.isCustomPerEventPrice ? services.snow.perEventPrice : calculatePerEventAutoPrice()}
                      onChange={e => {
                        handleServiceChange('snow', 'perEventPrice', numVal(e.target.value, true));
                        handleServiceChange('snow', 'isCustomPerEventPrice', true);
                      }}
                      InputProps={{ startAdornment: '$' }}
                      inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.perEventPrice', 'Per event price') }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, md: 4 }}>
                    {services.snow.isCustomPerEventPrice ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          handleServiceChange('snow', 'perEventPrice', calculatePerEventAutoPrice());
                          handleServiceChange('snow', 'isCustomPerEventPrice', false);
                        }}
                        aria-label={t('quoteForm.snow.revertToAutoCalc', 'Revert to auto-calculated price')}
                      >
                        {t('quoteForm.snow.revertToAutoCalc', 'Revert to Auto')}
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t('quoteForm.snow.autoCalculated', 'Auto-calculated from salting totals')}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Trash Bags */}
            {showCommercialSnowSections && (
              <Box sx={{ mb: 4, p: 3, bgcolor: '#fce4ec', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#c2185b', fontWeight: 'bold' }}>
                  {t('quoteForm.snow.trashBags', 'Trash Bags')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={services.snow.trashBagEnabled}
                          onChange={e => handleServiceChange('snow', 'trashBagEnabled', e.target.checked)}
                          aria-label="Enable snow trash bag service"
                        />
                      }
                      label={t('quoteForm.snow.trashBagService', 'Trash bags service')}
                    />
                    {/* Calculated seasonal amount, shown like the other snow
                        line items so it can be read off for the contract. */}
                    {services.snow.trashBagEnabled && (
                      <Typography variant="body2" color="success.main" sx={{ ml: 4 }} aria-live="polite">
                        {formatCurrencyWithSymbol(snowTrashBagEst)}
                        {!hasManualPrice(services.snow.trashBagPrice) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('quoteForm.snow.calculatedLabel', 'calculated')})
                          </Typography>
                        )}
                      </Typography>
                    )}
                  </Grid>
                  {services.snow.trashBagEnabled && (
                    <>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="snow-trash-type-label">{t('quoteForm.snow.bagType', 'Bag Type')}</InputLabel>
                          <Select
                            labelId="snow-trash-type-label"
                            value={services.snow.trashBagType}
                            onChange={e => handleServiceChange('snow', 'trashBagType', e.target.value)}
                            label={t('quoteForm.snow.bagType', 'Bag Type')}
                            inputProps={{ 'aria-label': t('quoteForm.snow.bagType', 'Snow trash bag disposal type') }}
                          >
                            <MenuItem value="OnSite">{t('quoteForm.snow.onSite', 'On Site')}</MenuItem>
                            <MenuItem value="OffSite">{t('quoteForm.snow.offSite', 'Off Site')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label={t('quoteForm.snow.numberOfBags', '# of Bags')}
                          value={services.snow.trashBagQuantity}
                          onChange={e => handleServiceChange('snow', 'trashBagQuantity', numVal(e.target.value))}
                          inputProps={{ min: 0, 'aria-label': t('quoteForm.snow.numberOfBags', 'Number of bags') }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label={t('quoteForm.snow.timePerWeek', 'Time (Week)')}
                          value={services.snow.trashBagTimePerWeek}
                          onChange={e => handleServiceChange('snow', 'trashBagTimePerWeek', numVal(e.target.value))}
                          inputProps={{ min: 0, 'aria-label': t('quoteForm.snow.timePerWeek', 'Times per week') }}
                        />
                      </Grid>
                    </>
                  )}
                  {/* TKT-297: Trash Bags had no price box at all. Manual override
                      of the per-bag calculation, kept visible without ticking the
                      checkbox so the line item doesn't read as price-less. */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('quoteForm.snow.trashBagPrice', 'Trash Bags Price')}
                      value={services.snow.trashBagPrice}
                      onChange={e => handleServiceChange('snow', 'trashBagPrice', numVal(e.target.value, true))}
                      helperText={t('quoteForm.snow.manualPriceHelper', 'Leave blank to use the calculated price.')}
                      InputProps={{ startAdornment: '$' }}
                      inputProps={{ min: 0, step: 0.01, 'aria-label': t('quoteForm.snow.trashBagPrice', 'Trash bags price') }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Snow Total from Backend */}
            {showCommercialSnowSections && (
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#2196f3', fontWeight: 'bold' }}>
                {t('quoteForm.snow.additionalOptions', 'Additional Options')}
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={services.snow.allInclusive || false}
                        onChange={e => handleServiceChange('snow', 'allInclusive', e.target.checked)}
                        inputProps={{ 'aria-label': t('quoteForm.snow.allInclusive', 'All-Inclusive Package') }}
                      />
                    }
                    label={t('quoteForm.snow.allInclusive', 'All-Inclusive Package')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quoteForm.snow.relocationPrice', 'Snow Relocation Price ($)')}
                    value={services.snow.relocationPrice || 0}
                    onChange={e => handleServiceChange('snow', 'relocationPrice', numVal(e.target.value))}
                    inputProps={{ 'aria-label': t('quoteForm.snow.relocationPrice', 'Snow Relocation Price ($)') }}
                  />
                </Grid>
              </Grid>
            </Box>
            )}

            <Box sx={{ mt: 3, p: 2, bgcolor: pricing.snow?.total > 0 ? '#2196f3' : '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: pricing.snow?.total > 0 ? 'white' : '#999', fontWeight: 'bold' }}>
                Snow Removal Total (Calculated)
              </Typography>
              <Typography variant="h5" sx={{ color: pricing.snow?.total > 0 ? 'white' : '#999', fontWeight: 'bold' }}>
                {formatCurrencyWithSymbol(pricing.snow?.total || 0)}
              </Typography>
            </Box>

            {/* Work Time Section */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#2196f3', fontWeight: 'bold' }}>
                {t('quoteForm.snow.workTime', 'Work Time')}
              </Typography>
              <Grid container spacing={2}>
                {showResidentialSnowPackages && services.snow.program > 0 && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.residentialWorkTime', 'Residential Snow Work Time (Mins)')}
                      value={services.snow.residentialWorkTimeMinutes || 0}
                      onChange={e => handleServiceChange('snow', 'residentialWorkTimeMinutes', numVal(e.target.value))}
                      inputProps={{ 'aria-label': t('quoteForm.snow.residentialWorkTime', 'Residential Snow Work Time (Mins)') }}
                    />
                  </Grid>
                )}
                {showCommercialSnowSections && (services.snow.parkingLotParkingLotPlowingEnabled || services.snow.parkingLotDrivewayPlowingEnabled) && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.plowingWorkTime', 'Plowing Work Time (Mins)')}
                      value={services.snow.plowingWorkTimeMinutes || 0}
                      onChange={e => handleServiceChange('snow', 'plowingWorkTimeMinutes', numVal(e.target.value))}
                      inputProps={{ 'aria-label': t('quoteForm.snow.plowingWorkTime', 'Plowing Work Time (Mins)') }}
                    />
                  </Grid>
                )}
                {showCommercialSnowSections && (services.snow.shovelingCitySidewalkEnabled || services.snow.shovelingInternalSidewalkEnabled || services.snow.shovelingDrivewayEnabled) && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.shovelingWorkTime', 'Shoveling Work Time (Mins)')}
                      value={services.snow.shovelingWorkTimeMinutes || 0}
                      onChange={e => handleServiceChange('snow', 'shovelingWorkTimeMinutes', numVal(e.target.value))}
                      inputProps={{ 'aria-label': t('quoteForm.snow.shovelingWorkTime', 'Shoveling Work Time (Mins)') }}
                    />
                  </Grid>
                )}
                {showCommercialSnowSections && (services.snow.truckParkingLotSaltingEnabled || services.snow.truckDrivewaySaltingEnabled || services.snow.crewCitySidewalkSaltingEnabled || services.snow.crewInternalSidewalkSaltingEnabled || services.snow.crewDrivewaySaltingEnabled) && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('quoteForm.snow.saltingWorkTime', 'Salting Work Time (Mins)')}
                      value={services.snow.saltingWorkTimeMinutes || 0}
                      onChange={e => handleServiceChange('snow', 'saltingWorkTimeMinutes', numVal(e.target.value))}
                      inputProps={{ 'aria-label': t('quoteForm.snow.saltingWorkTime', 'Salting Work Time (Mins)') }}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: '#1565c0' }}>
                    {t('quoteForm.snow.totalWorkTime', 'Total Work Time')}: {
                      (showResidentialSnowPackages ? (services.snow.residentialWorkTimeMinutes || 0) : 0) +
                      (showCommercialSnowSections ? (
                        (services.snow.plowingWorkTimeMinutes || 0) +
                        (services.snow.shovelingWorkTimeMinutes || 0) +
                        (services.snow.saltingWorkTimeMinutes || 0)
                      ) : 0)
                    } {t('quoteForm.snow.minutes', 'mins')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
        </>
      </Box>
    );
  };

  // Inspection Section
  const renderInspectionSection = () => (
    <Box>
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#5c6bc0', fontWeight: 'bold' }}>
          {t('quotes.inspection.urgentReminderTitle', 'Crew Urgent Reminder')}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label={t('quotes.inspection.urgentReminderEnglishLabel', 'English')}
          value={propertyInfo.urgent_reminder || ''}
          onChange={e => {
            setReminderTranslateError('');
            setPropertyInfo(prev => ({ ...prev, urgent_reminder: e.target.value }));
          }}
          helperText={t(
            'quotes.inspection.urgentReminderHelper',
            'This quote-level reminder is automatically added to route jobs created from this quote and must be acknowledged by the crew.'
          )}
          inputProps={{
            'aria-label': t('quotes.inspection.urgentReminderEnglishLabel', 'English'),
            maxLength: 1000,
          }}
        />
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={translatingReminder ? <CircularProgress size={14} /> : <TranslateIcon />}
            onClick={translateReminderToSpanish}
            disabled={translatingReminder || !(propertyInfo.urgent_reminder || '').trim()}
            aria-label={t('quotes.inspection.translateToMexicanSpanish', 'Translate to Mexican Spanish')}
          >
            {t('quotes.inspection.translateToMexicanSpanish', 'Translate to Mexican Spanish')}
          </Button>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={3}
          sx={{ mt: 2 }}
          label={t('quotes.inspection.urgentReminderSpanishLabel', 'Spanish (Mexico)')}
          value={propertyInfo.urgent_reminder_es || ''}
          onChange={e => {
            setReminderTranslateError('');
            setPropertyInfo(prev => ({ ...prev, urgent_reminder_es: e.target.value }));
          }}
          helperText={t(
            'quotes.inspection.urgentReminderSpanishHelper',
            'Optional — shown to crew who prefer Spanish. Leave blank to fall back to English.'
          )}
          inputProps={{
            'aria-label': t('quotes.inspection.urgentReminderSpanishLabel', 'Spanish (Mexico)'),
            maxLength: 1000,
          }}
        />
        {reminderTranslateError && (
          <Alert severity="error" sx={{ mt: 2 }} role="alert">
            {reminderTranslateError}
          </Alert>
        )}
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={services.inspection.enabled}
            onChange={e => handleServiceChange('inspection', 'enabled', e.target.checked)}
            inputProps={{ 'aria-label': t('quotes.inspection.enableLabel', 'Enable Property Inspection') }}
          />
        }
        label={t('quotes.inspection.enableLabel', 'Include Property Inspection')}
      />
      {services.inspection.enabled && (
        <>
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#5c6bc0', fontWeight: 'bold' }}>
              {t('quotes.inspection.timingTitle', 'Inspection Timing')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quotes.inspection.time1', 'Inspection Time 1 (Mins)')}
                  value={services.inspection.time1 || 0}
                  onChange={e => handleServiceChange('inspection', 'time1', numVal(e.target.value))}
                  inputProps={{ 'aria-label': t('quotes.inspection.time1', 'Inspection Time 1 (Mins)') }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quotes.inspection.time2', 'Inspection Time 2 (Mins)')}
                  value={services.inspection.time2 || 0}
                  onChange={e => handleServiceChange('inspection', 'time2', numVal(e.target.value))}
                  inputProps={{ 'aria-label': t('quotes.inspection.time2', 'Inspection Time 2 (Mins)') }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quotes.inspection.time3', 'Inspection Time 3 (Mins)')}
                  value={services.inspection.time3 || 0}
                  onChange={e => handleServiceChange('inspection', 'time3', numVal(e.target.value))}
                  inputProps={{ 'aria-label': t('quotes.inspection.time3', 'Inspection Time 3 (Mins)') }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('quotes.inspection.defaultPricing', 'Inspection Price ($)')}
                  value={services.inspection.defaultPricing || 0}
                  onChange={e => handleServiceChange('inspection', 'defaultPricing', numVal(e.target.value))}
                  inputProps={{ 'aria-label': t('quotes.inspection.defaultPricing', 'Inspection Price ($)') }}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );

  const total = pricing.grandTotal || 0;
  const enabledServices = Object.keys(services).filter(key => services[key] && services[key].enabled);

  // Legacy Residential/CommercialSummary: residential shows summer per month and
  // snow per season; commercial adds snow per month and a full-season monthly.
  // A season with no priced services (total <= $1, as in legacy) shows "--".
  const renderSeasonBreakdown = () => {
    const summerTotal = (pricing.seasonalTotal || 0) + (pricing.turf?.total || 0);
    const snowTotal = pricing.snow?.total || 0;
    const summerMonths = pricing.monthly?.summerMonths || 0;
    const winterMonths = pricing.monthly?.winterMonths || 0;
    const hasSummer = summerTotal > 1 && summerMonths > 0;
    const hasWinter = snowTotal > 1 && winterMonths > 0;
    const fullMonths = (hasSummer ? summerMonths : 0) + (hasWinter ? winterMonths : 0);
    const perMonth = t('quoteForm.seasonBreakdown.perMonth', 'per month');

    const boxes = [
      {
        key: 'summer',
        title: t('quoteForm.seasonBreakdown.summer', 'Summer'),
        months: hasSummer ? summerMonths : null,
        amount: hasSummer ? summerTotal / summerMonths : null,
        unit: perMonth,
        color: '#2e7d32',
        bg: '#e8f5e9'
      },
      {
        key: 'winter',
        title: t('quoteForm.seasonBreakdown.winter', 'Winter'),
        months: hasWinter ? winterMonths : null,
        amount: hasWinter ? (isResidential ? snowTotal : snowTotal / winterMonths) : null,
        unit: isResidential ? t('quoteForm.seasonBreakdown.perSeason', 'per season') : perMonth,
        color: '#1565c0',
        bg: '#e3f2fd'
      }
    ];
    if (!isResidential) {
      boxes.push({
        key: 'full',
        title: t('quoteForm.seasonBreakdown.fullSeason', 'Full Season'),
        months: fullMonths || null,
        amount: fullMonths ? total / fullMonths : null,
        unit: perMonth,
        color: '#000062',
        bg: '#eceff7'
      });
    }

    return (
      <Grid size={12}>
        <Box role="group" aria-label={t('quoteForm.seasonBreakdown.title', 'Monthly Payment Breakdown')}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('quoteForm.seasonBreakdown.title', 'Monthly Payment Breakdown')}
          </Typography>
          <Grid container spacing={2}>
            {boxes.map(box => {
              const monthsLabel = box.months
                ? t('quoteForm.seasonBreakdown.months', '{{count}} months', { count: box.months })
                : t('quoteForm.seasonBreakdown.noMonths', '-- months');
              const amountLabel = box.amount !== null ? formatCurrencyWithSymbol(box.amount) : '--';
              return (
                <Grid key={box.key} size={{ xs: 12, sm: isResidential ? 6 : 4 }}>
                  <Box
                    role="status"
                    aria-label={`${box.title}, ${monthsLabel}: ${amountLabel} ${box.amount !== null ? box.unit : ''}`}
                    sx={{ p: 2, bgcolor: box.bg, borderRadius: 1, borderLeft: `4px solid ${box.color}`, height: '100%', opacity: pricingLoading ? 0.7 : 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, color: box.color }}>
                      {box.title} ({monthsLabel})
                    </Typography>
                    <Typography variant="h6" sx={{ color: box.color }}>{amountLabel}</Typography>
                    {box.amount !== null && (
                      <Typography variant="caption" color="textSecondary">{box.unit}</Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Grid>
    );
  };

  // Commercial-specific render functions (inside component for state access)
  const renderParkingLotSection = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: '#607d8b', fontWeight: 'bold' }}>
        Parking Lot Services
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={services.parkingLot?.sweeping || false}
                onChange={e => handleServiceChange('parkingLot', 'sweeping', e.target.checked)}
              />
            }
            label="Parking Lot Sweeping"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Sweeping Frequency</InputLabel>
            <Select
              value={services.parkingLot?.sweepingFrequency || 'Weekly'}
              onChange={e => handleServiceChange('parkingLot', 'sweepingFrequency', e.target.value)}
              label="Sweeping Frequency"
            >
              <MenuItem value="Weekly">Weekly</MenuItem>
              <MenuItem value="BiWeekly">Bi-Weekly</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
            Additional parking lot services (line striping, crack filling, seal coating) can be configured here.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  // Billing Section
  const renderBillingSection = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: '#9c27b0', fontWeight: 'bold' }}>
        Commercial Billing Information
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="AR Contact Name"
            value={billingInfo.arContact}
            onChange={(e) => handleBillingInfoChange('arContact', e.target.value)}
            placeholder="Accounts payable contact person"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="AR Contact Phone"
            value={billingInfo.arPhone}
            onChange={(e) => handleBillingInfoChange('arPhone', e.target.value)}
            placeholder="Phone number"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="AR Contact Email"
            type="email"
            value={billingInfo.arEmail}
            onChange={(e) => handleBillingInfoChange('arEmail', e.target.value)}
            placeholder="Email address"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Department/Title"
            value={billingInfo.arName}
            onChange={(e) => handleBillingInfoChange('arName', e.target.value)}
            placeholder="Department or job title"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Payment Terms</InputLabel>
            <Select
              value={billingInfo.paymentTerms}
              label="Payment Terms"
              onChange={(e) => handleBillingInfoChange('paymentTerms', e.target.value)}
            >
              <MenuItem value="NET15">NET 15</MenuItem>
              <MenuItem value="NET30">NET 30</MenuItem>
              <MenuItem value="NET45">NET 45</MenuItem>
              <MenuItem value="NET60">NET 60</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Invoice Frequency</InputLabel>
            <Select
              value={billingInfo.invoiceFrequency}
              label="Invoice Frequency"
              onChange={(e) => handleBillingInfoChange('invoiceFrequency', e.target.value)}
            >
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Quarterly">Quarterly</MenuItem>
              <MenuItem value="Annually">Annually</MenuItem>
              <MenuItem value="PerService">Per Service</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: config.color }}>
        {isCommercial ? <BusinessIcon /> : <LawnIcon />}
        {quote ? `Edit ${config.title}` : `Create ${config.title}`}
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress size={28} sx={{ mb: 2 }} />}

      <Box sx={{ mb: 3 }}>
        {serviceSections.map((section) => (
          <Accordion 
            key={section.key}
            expanded={expandedSections.includes(section.key)}
            onChange={() => toggleSection(section.key)}
            sx={{ 
              mb: 1,
              '&:before': { display: 'none' },
              border: section.required && !formData.customer_id ? '1px solid #f44336' : '1px solid #e0e0e0'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box sx={{ color: section.color }}>
                  {section.icon}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ color: section.color }}>
                    {section.title}
                    {section.required && <span style={{ color: '#f44336' }}> *</span>}
                  </Typography>
                  {section.description && (
                    <Typography variant="body2" color="textSecondary">
                      {section.description}
                    </Typography>
                  )}
                  {/* Show customer info when Customer section is collapsed */}
                  {section.key === 'customer' && !expandedSections.includes('customer') && selectedCustomer && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000062' }}>
                        {getCustomerDisplayName(selectedCustomer)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                        {getCustomerAddress(selectedCustomer) && (
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                            📍 {getCustomerAddress(selectedCustomer)}
                          </Typography>
                        )}
                        {selectedCustomer.email_address && (
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                            📧 {selectedCustomer.email_address}
                          </Typography>
                        )}
                        {selectedCustomer.phone_number && (
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                            📞 {selectedCustomer.phone_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
                {section.key !== 'customer' && services[section.key]?.enabled && (
                  <Chip 
                    label="Enabled" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                  />
                )}
                {section.key === 'customer' && selectedCustomer && (
                  <Chip 
                    label="Selected" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {section.key === 'customer' && renderCustomerSection()}
              {section.key === 'property' && renderPropertySection()}
              {section.key === 'lawn' && renderLawnSection()}
              {section.key === 'parkingLot' && isCommercial && renderParkingLotSection()}
              {section.key === 'gardening' && renderGardeningSection()}
              {section.key === 'spring' && renderSpringSection()}
              {section.key === 'fall' && renderFallSection()}
              {section.key === 'trimming' && renderTrimmingSection()}
              {section.key === 'annuals' && renderAnnualsSection()}
              {section.key === 'sweeping' && renderSweepingSection()}
              {section.key === 'turf' && renderTurfSection()}
              {section.key === 'snow' && renderSnowSection()}
              {section.key === 'inspection' && renderInspectionSection()}
              {section.key === 'billing' && isCommercial && renderBillingSection()}
              {!['customer', 'property', 'lawn', 'parkingLot', 'gardening', 'spring', 'fall', 'trimming', 'annuals', 'sweeping', 'turf', 'snow', 'inspection', 'billing'].includes(section.key) && (
                <Typography color="textSecondary">
                  {section.title} configuration will be implemented soon.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Site Diagram Section - Right after Property Information */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {(quote?.id || createdQuoteId) ? (
          <SiteDiagramUpload 
            quoteId={quote?.id || createdQuoteId}
            readOnly={false} 
            address={selectedCustomer ? `${selectedCustomer.street_address || ''} ${selectedCustomer.city || ''} ${selectedCustomer.province || ''} ${selectedCustomer.postal_code || ''}`.trim() : ''}
            onValuesCalculated={handleSiteDiagramValues}
          />
        ) : (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('quotes.siteDiagram')}
            </Typography>
            <input
              accept="image/*"
              id="new-quote-site-diagram"
              type="file"
              onChange={handlePendingSiteDiagram}
              style={{ display: 'none' }}
              aria-label={t('quotes.siteDiagramSelect')}
            />
            <label htmlFor="new-quote-site-diagram">
              <Button
                component="span"
                variant="outlined"
                startIcon={<UploadIcon />}
                aria-label={t('quotes.siteDiagramSelect')}
              >
                {pendingSiteDiagram
                  ? t('quotes.siteDiagramReplaceSelection')
                  : t('quotes.siteDiagramSelect')}
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('quotes.siteDiagramPendingHelper')}
            </Typography>
            {pendingSiteDiagramPreview && (
              <Box
                component="img"
                src={pendingSiteDiagramPreview}
                alt={t('quotes.siteDiagramPreviewAlt')}
                sx={{
                  display: 'block',
                  width: '100%',
                  maxWidth: 520,
                  maxHeight: 300,
                  objectFit: 'contain',
                  mt: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              />
            )}
            {pendingSiteDiagram && (
              <Alert
                severity="success"
                icon={<InfoOutlined />}
                sx={{ mt: 2 }}
                aria-label={t('quotes.siteDiagramReady')}
            >
                {t('quotes.siteDiagramReady', { filename: pendingSiteDiagram.name })}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Quote Summary</Typography>

        {/* TKT-433: price this quote with a saved price list (snapshot), or
            leave it on today's prices. */}
        <Box sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {t('quoteForm.priceList.title', 'Price List')}
          </Typography>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 320 } }}>
            <InputLabel id="quote-price-list-label" shrink>{t('quoteForm.priceList.label', 'Price list')}</InputLabel>
            <Select
              labelId="quote-price-list-label"
              label={t('quoteForm.priceList.label', 'Price list')}
              value={formData.price_snapshot_id ? String(formData.price_snapshot_id) : ''}
              onChange={(e) => handlePriceSnapshotChange(e.target.value ? Number(e.target.value) : '')}
              disabled={ratesLoading}
              displayEmpty
              notched
            >
              <MenuItem value="">{t('quoteForm.priceList.current', "Current prices (today's values)")}</MenuItem>
              {priceSnapshots.map((snap) => (
                <MenuItem key={snap.id} value={String(snap.id)}>{snap.name}</MenuItem>
              ))}
              {formData.price_snapshot_id && !priceSnapshots.some((snap) => snap.id === Number(formData.price_snapshot_id)) && (
                <MenuItem value={String(formData.price_snapshot_id)}>
                  {t('quoteForm.priceList.missing', 'Saved price list (no longer available)')}
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('quoteForm.priceList.help', "Pick a saved price list to price this quote with those prices, for example a quote sent before a price change. Leave it on Current prices to use today's prices.")}
          </Typography>
        </Box>

        {!isConstruction && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {t('quoteForm.renewal.title', 'Renewal Price Adjustment')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('quoteForm.renewal.scope', 'Applies to every calculated price on this quote, including manual overrides. A new percentage replaces the previous one. It does not compound. Reset removes the adjustment.')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <TextField
              label={t('quoteForm.renewal.percentage', 'Percentage')}
              type="text"
              inputMode="decimal"
              size="small"
              value={adjustmentDraft}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) setAdjustmentDraft(val);
              }}
              InputProps={{
                endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography>
              }}
              inputProps={{ 'aria-label': t('quoteForm.renewal.percentage', 'Percentage') }}
              sx={{ width: { xs: '100%', sm: 160 } }}
              helperText={formData.is_auto_custom_percentage_active
                ? t('quoteForm.renewal.active', 'Active at {{pct}}%', { pct: formData.auto_custom_percentage })
                : t('quoteForm.renewal.inactive', 'No adjustment active')}
            />
            <Button
              variant="contained"
              onClick={applyPriceAdjustment}
              disabled={adjustmentSaving}
              aria-label={t('quoteForm.renewal.update', 'Update Price Adjustment')}
            >
              {t('quoteForm.renewal.update', 'Update Price Adjustment')}
            </Button>
            <Button
              variant="outlined"
              onClick={resetPriceAdjustment}
              disabled={adjustmentSaving || !formData.is_auto_custom_percentage_active}
              aria-label={t('quoteForm.renewal.reset', 'Reset Price Adjustment')}
            >
              {t('quoteForm.renewal.reset', 'Reset Price Adjustment')}
            </Button>
          </Box>
          {adjustmentHistory.length > 0 && (
            <Box component="ul" sx={{ mt: 2, mb: 0, pl: 2 }} aria-label={t('quoteForm.renewal.history', 'Price adjustment history')}>
              {adjustmentHistory.map((row) => (
                <Typography component="li" variant="body2" key={row.id} sx={{ mb: 0.5 }}>
                  {row.created_by_name} · {row.created_at ? new Date(row.created_at).toLocaleString() : ''} · {row.percentage}% · {row.adjustment_type === 'reset'
                    ? t('quoteForm.renewal.resetType', 'Reset')
                    : t('quoteForm.renewal.updateType', 'Update')}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2">
              <strong>Services Enabled:</strong> {enabledServices.length}
            </Typography>
            {enabledServices.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {enabledServices.map(key => (
                  <Chip 
                    key={key} 
                    label={serviceSections.find(s => s.key === key)?.title || key}
                    size="small" 
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" align="right" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              Estimated Total: 
              <span style={{ opacity: pricingLoading ? 0.7 : 1, transition: 'opacity 0.2s' }}>
                {`$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </span>
              {pricingLoading && <CircularProgress size={14} thickness={5} sx={{ ml: 1 }} />}
            </Typography>
            {pricingError && (
              <Typography variant="body2" align="right" color="error" sx={{ mb: 1 }}>
                Error calculating pricing: {pricingError}
              </Typography>
            )}
            
            <Typography variant="body2" align="right" color="textSecondary" sx={{ mt: 1 }}>
              {(() => {
                const snap = priceSnapshots.find((s) => s.id === Number(formData.price_snapshot_id));
                return formData.price_snapshot_id
                  ? t('quoteForm.priceList.usingSnapshot', '*Priced with the "{{name}}" price list', {
                    name: snap?.name || t('quoteForm.priceList.savedName', 'saved')
                  })
                  : t('quoteForm.priceList.usingCurrent', '*Pricing calculated in real-time based on current rates');
              })()}
            </Typography>
          </Grid>
          {!isConstruction && pricing.monthly && renderSeasonBreakdown()}
        </Grid>
      </Paper>


      <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
        <Button onClick={onCancel} startIcon={<Cancel />}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={onSubmit} 
          startIcon={<Save />} 
          disabled={loading || !selectedCustomer}
        >
          {quote ? 'Update Quote' : 'Create Quote'}
        </Button>
      </Box>
    </Box>
  );
};

export default UniversalQuoteForm;
